import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { User, Phone, Lock, Building2, CreditCard, Users, LogOut, Eye, EyeOff, Shield, AlertCircle, CheckCircle, Clock, Banknote, Copy, Upload, Image, X, Youtube, Unlink, Trash2, Share2, ExternalLink, Crown, Star, Camera, Loader2, ClipboardList, Settings, Rocket, Coins, Bell, BellOff } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const UserPage = () => {
  const navigate = useNavigate();
  const { user, admin, isAdmin, login, adminLogin, register, logout, isAuthenticated, token, refreshUser } = useAuth();
  const [searchParams] = useSearchParams();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [authTab, setAuthTab] = useState("login");
  const [isAdminLogin, setIsAdminLogin] = useState(false);
  const [youtubeStatus, setYoutubeStatus] = useState({ connected: false, channel: null });
  const fileInputRef = useRef(null);
  const proReceiptRef = useRef(null);
  const assignedTaskRef = useRef(null);
  
  // Subscription state
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [paymentReference, setPaymentReference] = useState("");
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [receiptFile, setReceiptFile] = useState(null);
  const [receiptPreview, setReceiptPreview] = useState(null);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPhone, setForgotPhone] = useState("");
  const [shareCampaigns, setShareCampaigns] = useState([]);
  const [completedCampaigns, setCompletedCampaigns] = useState([]);
  
  // Bank info state
  const [showBankForm, setShowBankForm] = useState(false);
  const [bankForm, setBankForm] = useState({ bank_name: "", bank_account: "", bank_holder: "", account_type: "ahorro" });
  const [savingBank, setSavingBank] = useState(false);
  
  // User photo state
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const userPhotoInputRef = useRef(null);
  
  // Pro upgrade state
  const [proStatus, setProStatus] = useState(null);
  const [showProUpgrade, setShowProUpgrade] = useState(false);
  const [uploadingProReceipt, setUploadingProReceipt] = useState(false);
  
  // Assigned tasks state
  const [assignedTasks, setAssignedTasks] = useState([]);
  const [uploadingTaskScreenshot, setUploadingTaskScreenshot] = useState({});
  const [registerType, setRegisterType] = useState("recluta"); // "recluta" or "pro"
  
  // Daily visits state
  const [dailyVisits, setDailyVisits] = useState([]);
  const [dailyVisitsStats, setDailyVisitsStats] = useState({ total_completed_today: 0, total_pending: 0 });
  const [uploadingVisitScreenshot, setUploadingVisitScreenshot] = useState({});
  const [visitTimers, setVisitTimers] = useState({}); // {configId: {started: true, elapsed: 0, canSubmit: false}}
  const visitScreenshotRefs = useRef({});
  
  // Upcoming launches state
  const [upcomingLaunches, setUpcomingLaunches] = useState([]);
  const [launchSubmissions, setLaunchSubmissions] = useState({}); // {eventId: submission}
  const [uploadingLaunchScreenshot, setUploadingLaunchScreenshot] = useState({});
  const launchScreenshotRefs = useRef({});
  
  // Push notification state
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushSupported, setPushSupported] = useState(false);

  // Inbox (buzón) state
  const [inboxSubmissions, setInboxSubmissions] = useState([]);
  const [inboxNote, setInboxNote] = useState("");
  const [inboxArtistCode, setInboxArtistCode] = useState("");
  const [inboxActionType, setInboxActionType] = useState("suscriptor");
  const [inboxCounts, setInboxCounts] = useState({ visita: 0, comentario: 0, like: 0, suscriptor: 0, like_comentario: 0, combo: 0, total: 0 });
  const [submittingInbox, setSubmittingInbox] = useState(false);
  const inboxFileInputRef = useRef(null);

  const [loginForm, setLoginForm] = useState({ phone: "", password: "" });
  const [registerForm, setRegisterForm] = useState({
    phone: "",
    nickname: "",
    country: "",
    city: "",
    password: "",
    confirm_password: "",
    referral_code: "",
    user_tier: "recluta"
  });

  // Fetch user data when logged in
  useEffect(() => {
    const fetchUserData = async () => {
      if (isAuthenticated && !isAdmin && token) {
        try {
          const [infoRes, statusRes, ytRes, campaignsRes, proRes, tasksRes, visitsRes] = await Promise.all([
            axios.get(`${API}/subscription/info`).catch(() => ({ data: {} })),
            axios.get(`${API}/subscription/status`, {
              headers: { Authorization: `Bearer ${token}` }
            }).catch(() => ({ data: null })),
            axios.get(`${API}/auth/youtube/status`, {
              headers: { Authorization: `Bearer ${token}` }
            }).catch(() => ({ data: null })),
            axios.get(`${API}/share-campaigns`).catch(() => ({ data: [] })),
            axios.get(`${API}/user/pro-status`, {
              headers: { Authorization: `Bearer ${token}` }
            }).catch(() => ({ data: { user_tier: "recluta", pro_status: "none" } })),
            axios.get(`${API}/user/my-assigned-tasks`, {
              headers: { Authorization: `Bearer ${token}` }
            }).catch(() => ({ data: [] })),
            axios.get(`${API}/daily-visits/my-progress`, {
              headers: { Authorization: `Bearer ${token}` }
            }).catch(() => ({ data: { visits: [], stats: { total_completed_today: 0, total_pending: 0 } } }))
          ]);
          setSubscriptionInfo(infoRes.data);
          setSubscriptionStatus(statusRes.data);
          setYoutubeStatus(ytRes.data);
          setShareCampaigns(campaignsRes.data);
          setProStatus(proRes.data);
          setAssignedTasks(tasksRes.data);
          setDailyVisits(visitsRes.data.visits || []);
          setDailyVisitsStats(visitsRes.data.stats || { total_completed_today: 0, total_pending: 0 });
          // Inbox submissions
          try {
            const [inboxRes, countsRes] = await Promise.all([
              axios.get(`${API}/user/inbox/my`, { headers: { Authorization: `Bearer ${token}` } }),
              axios.get(`${API}/user/inbox/counts`, { headers: { Authorization: `Bearer ${token}` } }),
            ]);
            setInboxSubmissions(inboxRes.data);
            setInboxCounts(countsRes.data);
          } catch (e) {
            console.error("Error fetching inbox:", e);
          }
        } catch (error) {
          console.error("Error fetching user data:", error);
        }
      }
    };
    fetchUserData();
  }, [isAuthenticated, isAdmin, token]);

  // Pro upgrade functions
  const handleRequestProUpgrade = async () => {
    try {
      const response = await axios.post(`${API}/user/upgrade-to-pro`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      setShowProUpgrade(true);
      // Refresh pro status
      const proRes = await axios.get(`${API}/user/pro-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProStatus(proRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al solicitar upgrade");
    }
  };

  const handleUploadProReceipt = async (file) => {
    if (!file || !proStatus?.upgrade_request?.id) return;
    
    setUploadingProReceipt(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      await axios.post(
        `${API}/user/pro-upload-receipt/${proStatus.upgrade_request.id}`,
        formData,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
      );
      toast.success("Comprobante subido. Pendiente de aprobación.");
      setShowProUpgrade(false);
      // Refresh pro status
      const proRes = await axios.get(`${API}/user/pro-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProStatus(proRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al subir comprobante");
    } finally {
      setUploadingProReceipt(false);
    }
  };

  // Assigned task functions
  const handleCompleteAssignedTask = async (taskId, file) => {
    if (!file) return;
    
    setUploadingTaskScreenshot(prev => ({ ...prev, [taskId]: true }));
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      await axios.post(
        `${API}/user/assigned-tasks/${taskId}/complete`,
        formData,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
      );
      toast.success("Tarea completada. Pendiente de aprobación.");
      // Refresh tasks
      const tasksRes = await axios.get(`${API}/user/my-assigned-tasks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAssignedTasks(tasksRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al completar tarea");
    } finally {
      setUploadingTaskScreenshot(prev => ({ ...prev, [taskId]: false }));
    }
  };

  // Inbox (buzón) function: user uploads a proof screenshot
  const handleInboxSubmit = async (file) => {
    if (!file) return;
    if (!inboxArtistCode.trim()) {
      toast.error("Escribe el código del artista antes de subir la captura");
      if (inboxFileInputRef.current) inboxFileInputRef.current.value = "";
      return;
    }
    setSubmittingInbox(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('note', inboxNote || "");
      formData.append('artist_code', inboxArtistCode.trim());
      formData.append('action_type', inboxActionType);
      const res = await axios.post(`${API}/user/inbox/submit`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      toast.success(res.data.message || "Captura enviada");
      setInboxNote("");
      setInboxArtistCode("");
      const [inboxRes, countsRes] = await Promise.all([
        axios.get(`${API}/user/inbox/my`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/user/inbox/counts`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setInboxSubmissions(inboxRes.data);
      setInboxCounts(countsRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al enviar la captura");
    } finally {
      setSubmittingInbox(false);
      if (inboxFileInputRef.current) inboxFileInputRef.current.value = "";
    }
  };

  // Daily visit functions
  const handleCompleteDailyVisit = async (configId, file) => {
    if (!file) return;
    
    setUploadingVisitScreenshot(prev => ({ ...prev, [configId]: true }));
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      await axios.post(
        `${API}/daily-visits/${configId}/upload-screenshot`,
        formData,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
      );
      toast.success("Visita enviada. Pendiente de aprobación.");
      // Refresh visits
      const visitsRes = await axios.get(`${API}/daily-visits/my-progress`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDailyVisits(visitsRes.data.visits || []);
      setDailyVisitsStats(visitsRes.data.stats || { total_completed_today: 0, total_pending: 0 });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al enviar visita");
    } finally {
      setUploadingVisitScreenshot(prev => ({ ...prev, [configId]: false }));
    }
  };

  const getPlatformIcon = (platform) => {
    switch (platform) {
      case "youtube": return <Youtube className="w-4 h-4 text-red-500" />;
      case "instagram": return <Camera className="w-4 h-4 text-pink-500" />;
      case "tiktok": return <Share2 className="w-4 h-4 text-cyan-400" />;
      case "facebook": return <Users className="w-4 h-4 text-blue-500" />;
      default: return <ExternalLink className="w-4 h-4 text-gray-400" />;
    }
  };

  // Start visit timer and open link
  const handleStartVisit = async (configId, targetUrl) => {
    try {
      await axios.post(`${API}/daily-visits/${configId}/start-timer`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Open the URL in a new tab
      window.open(targetUrl, '_blank');
      
      // Start local timer
      setVisitTimers(prev => ({
        ...prev,
        [configId]: { started: true, startTime: Date.now(), elapsed: 0, canSubmit: false }
      }));
      
      toast.success("⏱️ Timer iniciado. Mínimo 1:20 en la visita.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al iniciar visita");
    }
  };

  // Check timer status
  const checkVisitTimer = async (configId) => {
    try {
      const res = await axios.get(`${API}/daily-visits/${configId}/check-timer`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setVisitTimers(prev => ({
        ...prev,
        [configId]: {
          started: res.data.timer_started,
          elapsed: res.data.elapsed_seconds,
          elapsedFormatted: res.data.elapsed_formatted,
          canSubmit: res.data.can_submit,
          message: res.data.message
        }
      }));
      
      return res.data;
    } catch (error) {
      console.error("Error checking timer:", error);
      return null;
    }
  };

  // Fetch upcoming launches
  const fetchUpcomingLaunches = async () => {
    try {
      const response = await axios.get(`${API}/launch-events/upcoming`);
      setUpcomingLaunches(response.data);
      
      // Fetch submission status for each launch if user is authenticated
      if (token) {
        const submissions = {};
        for (const launch of response.data) {
          try {
            const subRes = await axios.get(`${API}/launch-events/${launch.id}/my-submission`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            if (subRes.data) {
              submissions[launch.id] = subRes.data;
            }
          } catch (e) {
            // No submission yet
          }
        }
        setLaunchSubmissions(submissions);
      }
    } catch (error) {
      console.error("Error fetching launches:", error);
    }
  };

  // Handle join launch
  const handleJoinLaunch = async (eventId) => {
    if (!token) {
      toast.error("Inicia sesión para unirte al lanzamiento");
      return;
    }
    try {
      const response = await axios.post(
        `${API}/launch-events/${eventId}/join`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message || "¡Te has unido al lanzamiento!");
      if (response.data.youtube_url) {
        window.open(response.data.youtube_url, "_blank");
      }
      fetchUpcomingLaunches();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al unirse al lanzamiento");
    }
  };

  // Handle upload launch screenshot
  const handleUploadLaunchScreenshot = async (eventId, file) => {
    if (!file || !token) return;
    
    setUploadingLaunchScreenshot(prev => ({ ...prev, [eventId]: true }));
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(
        `${API}/launch-events/${eventId}/upload-screenshot`,
        formData,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
      );
      
      toast.success(response.data.message || "Captura subida exitosamente");
      fetchUpcomingLaunches();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al subir captura");
    } finally {
      setUploadingLaunchScreenshot(prev => ({ ...prev, [eventId]: false }));
    }
  };

  // Handle confirm attendance (Pro users only)
  const handleConfirmAttendance = async (eventId) => {
    if (!token) {
      toast.error("Inicia sesión para confirmar asistencia");
      return;
    }

    if (user?.user_tier !== "pro" && user?.tier !== "pro") {
      toast.error("Solo usuarios Pro pueden confirmar asistencia", {
        description: "Actualiza a Pro para acceder a esta función"
      });
      return;
    }

    try {
      const response = await axios.post(
        `${API}/launch-events/${eventId}/confirm-attendance`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message, {
        description: "¡Te notificaremos cuando comience el lanzamiento!"
      });
      
      fetchUpcomingLaunches();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al confirmar asistencia");
    }
  };

  // Check if push notifications are supported and enabled
  useEffect(() => {
    const checkPushSupport = async () => {
      if ('serviceWorker' in navigator && 'PushManager' in window) {
        setPushSupported(true);
        
        // Check if already subscribed
        if (isAuthenticated && token) {
          try {
            const response = await axios.get(`${API}/push/status`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            setPushEnabled(response.data.subscribed);
          } catch (e) {
            console.log("Could not check push status");
          }
        }
      }
    };
    checkPushSupport();
  }, [isAuthenticated, token]);

  // Function to subscribe to push notifications
  const subscribeToPush = async () => {
    try {
      // Register service worker
      const registration = await navigator.serviceWorker.register('/sw.js');
      console.log('Service Worker registered:', registration);
      
      // Request notification permission
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        toast.error('Debes permitir notificaciones para recibir alertas de lanzamientos');
        return;
      }
      
      // Get VAPID public key
      const vapidKey = process.env.REACT_APP_VAPID_PUBLIC_KEY;
      
      // Convert VAPID key to Uint8Array
      const urlBase64ToUint8Array = (base64String) => {
        const padding = '='.repeat((4 - base64String.length % 4) % 4);
        const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
        const rawData = window.atob(base64);
        const outputArray = new Uint8Array(rawData.length);
        for (let i = 0; i < rawData.length; ++i) {
          outputArray[i] = rawData.charCodeAt(i);
        }
        return outputArray;
      };
      
      // Subscribe to push
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey)
      });
      
      // Send subscription to backend
      const response = await axios.post(`${API}/push/subscribe`, {
        endpoint: subscription.endpoint,
        keys: {
          p256dh: btoa(String.fromCharCode.apply(null, new Uint8Array(subscription.getKey('p256dh')))),
          auth: btoa(String.fromCharCode.apply(null, new Uint8Array(subscription.getKey('auth'))))
        }
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setPushEnabled(true);
      toast.success('🔔 ' + response.data.message);
      
    } catch (error) {
      console.error('Push subscription error:', error);
      toast.error('Error al activar notificaciones: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Function to unsubscribe from push
  const unsubscribeFromPush = async () => {
    try {
      // Unsubscribe from browser
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      if (subscription) {
        await subscription.unsubscribe();
      }
      
      // Remove from backend
      await axios.delete(`${API}/push/unsubscribe`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setPushEnabled(false);
      toast.success('Notificaciones desactivadas');
      
    } catch (error) {
      console.error('Push unsubscribe error:', error);
      toast.error('Error al desactivar notificaciones');
    }
  };

  // Update timers periodically
  useEffect(() => {
    if (!isAuthenticated || !token) return;
    
    const interval = setInterval(() => {
      // Update local elapsed time for started timers
      setVisitTimers(prev => {
        const updated = { ...prev };
        Object.keys(updated).forEach(configId => {
          if (updated[configId].started && updated[configId].startTime) {
            const elapsed = Math.floor((Date.now() - updated[configId].startTime) / 1000);
            updated[configId].elapsed = elapsed;
            updated[configId].elapsedFormatted = `${Math.floor(elapsed / 60)}:${(elapsed % 60).toString().padStart(2, '0')}`;
            updated[configId].canSubmit = elapsed >= 80;
          }
        });
        return updated;
      });
    }, 1000);
    
    return () => clearInterval(interval);
  }, [isAuthenticated, token]);

  // Check for YouTube connection callback
  useEffect(() => {
    if (searchParams.get("youtube_connected") === "true") {
      toast.success("¡YouTube conectado exitosamente!");
      // Refresh YouTube status
      if (token) {
        axios.get(`${API}/auth/youtube/status`, {
          headers: { Authorization: `Bearer ${token}` }
        }).then(res => setYoutubeStatus(res.data));
      }
    }
  }, [searchParams, token]);

  const handleConnectYoutube = async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API}/auth/google?user_id=${user.id}`);
      // Open in same window for redirect
      window.location.href = response.data.auth_url;
    } catch (error) {
      toast.error("Error al conectar con YouTube");
      setLoading(false);
    }
  };

  const handleDisconnectYoutube = async () => {
    setLoading(true);
    try {
      await axios.delete(`${API}/auth/youtube/disconnect`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setYoutubeStatus({ connected: false, channel: null });
      toast.success("YouTube desconectado");
    } catch (error) {
      toast.error("Error al desconectar YouTube");
    } finally {
      setLoading(false);
    }
  };

  const handleRequestSubscription = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/subscription/request`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Solicitud de inscripción creada");
      setShowPaymentForm(true);
      // Refresh status
      const statusRes = await axios.get(`${API}/subscription/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscriptionStatus(statusRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al crear solicitud");
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!['image/jpeg', 'image/png', 'image/jpg', 'image/webp'].includes(file.type)) {
        toast.error("Solo se permiten imágenes JPG, PNG o WEBP");
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error("El archivo no puede ser mayor a 5MB");
        return;
      }
      setReceiptFile(file);
      setReceiptPreview(URL.createObjectURL(file));
    }
  };

  const handleUploadReceipt = async () => {
    if (!receiptFile) {
      toast.error("Selecciona una imagen del comprobante");
      return;
    }
    setLoading(true);
    try {
      const paymentId = subscriptionStatus?.payment?.id;
      const formData = new FormData();
      formData.append('file', receiptFile);
      
      await axios.post(
        `${API}/subscription/upload-receipt/${paymentId}`,
        formData,
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          } 
        }
      );
      toast.success("Comprobante subido exitosamente. El administrador verificará tu pago.");
      setReceiptFile(null);
      setReceiptPreview(null);
      // Refresh status
      const statusRes = await axios.get(`${API}/subscription/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscriptionStatus(statusRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al subir comprobante");
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!forgotPhone.trim()) {
      toast.error("Ingresa tu número de teléfono");
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/request-password-reset?phone=${forgotPhone}`);
      const { whatsapp_number, user_name } = response.data;
      const message = `Hola, soy ${user_name}. Necesito recuperar mi contraseña. Mi teléfono es: ${forgotPhone}`;
      const whatsappUrl = `https://wa.me/${whatsapp_number}?text=${encodeURIComponent(message)}`;
      window.open(whatsappUrl, '_blank');
      toast.success("Contacta al administrador por WhatsApp para recuperar tu contraseña");
      setShowForgotPassword(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al solicitar recuperación");
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isAdminLogin) {
        await adminLogin(loginForm.phone, loginForm.password);
        toast.success("¡Bienvenido Administrador!");
      } else {
        await login(loginForm.phone, loginForm.password);
        toast.success("¡Inicio de sesión exitoso!");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al iniciar sesión");
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!registerForm.nickname) {
      toast.error("Ingresa tu apodo");
      return;
    }
    if (registerForm.password !== registerForm.confirm_password) {
      toast.error("Las contraseñas no coinciden");
      return;
    }
    if (registerForm.password.length < 6) {
      toast.error("La contraseña debe tener al menos 6 caracteres");
      return;
    }
    setLoading(true);
    try {
      const { confirm_password, ...dataToSend } = registerForm;
      const result = await register(dataToSend);
      if (result.referral_code) {
        toast.info(`Tu código de referido: ${result.referral_code}`);
      }
      toast.success(result.message || "Registro recibido. Tu cuenta está pendiente de aprobación del administrador.", { duration: 7000 });
      setRegisterForm({ phone: "", nickname: "", country: "", city: "", password: "", confirm_password: "", referral_code: "", user_tier: "recluta" });
      setAuthTab("login");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al registrarse");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    toast.success("Sesión cerrada");
  };

  const handleShareCampaign = async (campaignId) => {
    try {
      const response = await axios.post(
        `${API}/share-campaigns/${campaignId}/complete`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      setCompletedCampaigns([...completedCampaigns, campaignId]);
      // Refresh campaigns
      const campaignsRes = await axios.get(`${API}/share-campaigns`).catch(() => ({ data: [] }));
      setShareCampaigns(campaignsRes.data);
      refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al completar la tarea");
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("URL copiada al portapapeles");
  };

  const handleSaveBankInfo = async () => {
    if (!bankForm.bank_name || !bankForm.bank_account || !bankForm.bank_holder) {
      toast.error("Completa todos los campos requeridos");
      return;
    }
    setSavingBank(true);
    try {
      await axios.put(`${API}/users/me/bank-info`, bankForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Datos bancarios guardados");
      setShowBankForm(false);
      refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar datos bancarios");
    } finally {
      setSavingBank(false);
    }
  };

  const handleUploadUserPhoto = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Solo se permiten imágenes (JPG, PNG, WEBP)");
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("La imagen no puede superar 5MB");
      return;
    }
    
    setUploadingPhoto(true);
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      await axios.post(`${API}/users/me/upload-photo`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });
      toast.success("Foto de perfil actualizada");
      refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al subir la foto");
    } finally {
      setUploadingPhoto(false);
      if (userPhotoInputRef.current) {
        userPhotoInputRef.current.value = "";
      }
    }
  };

  // Fetch launches periodically and on visibility change
  useEffect(() => {
    fetchUpcomingLaunches();
    
    // Check if there are active launches (started but not expired)
    const hasActiveLaunch = upcomingLaunches.some(l => l.has_started && !l.task_expired);
    
    // Update more frequently during active launches (every 5 seconds)
    // Otherwise every 30 seconds
    const intervalTime = hasActiveLaunch ? 5000 : 30000;
    const interval = setInterval(fetchUpcomingLaunches, intervalTime);
    
    // Update immediately when user returns to the page (e.g., from YouTube)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('🔄 User returned to page, refreshing launches...');
        fetchUpcomingLaunches();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [upcomingLaunches.length]);

  const openShareWindow = (platform, url) => {
    const encodedUrl = encodeURIComponent(url);
    const shareUrls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
      twitter: `https://twitter.com/intent/tweet?url=${encodedUrl}`,
      whatsapp: `https://wa.me/?text=${encodedUrl}`,
      telegram: `https://t.me/share/url?url=${encodedUrl}`
    };
    window.open(shareUrls[platform], '_blank', 'width=600,height=400');
  };

  if (isAuthenticated) {
    const currentUser = isAdmin ? admin : user;
    // Prefer FRESH data from the backend (so admin changes like cancelling a Pro
    // inscription reflect on the user's next page load without needing to re-login).
    const isSubscribed = (subscriptionStatus?.is_subscribed ?? user?.is_subscribed) || false;
    const subStatus = subscriptionStatus?.subscription_status || user?.subscription_status;
    const userTier = proStatus?.user_tier || user?.user_tier || "recluta";
    const isRecluta = userTier === "recluta";
    
    // Show Recluta user dashboard with assigned tasks only
    if (!isAdmin && !isSubscribed && isRecluta) {
      return (
        <div className="min-h-screen pb-20 p-4 animate-fadeInUp" data-testid="user-recluta">
          <div className="max-w-md mx-auto space-y-6">
            {/* User Header */}
            <Card className="bg-gradient-to-br from-[#22C55E]/20 to-[#10B981]/20 border-white/10">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 rounded-full bg-[#22C55E] flex items-center justify-center mx-auto mb-4">
                  <User className="w-8 h-8 text-white" />
                </div>
                <h2 className="text-xl font-bold">{currentUser?.name} {currentUser?.last_name}</h2>
                <p className="text-gray-400">{currentUser?.phone}</p>
                {currentUser?.city && (
                  <p className="text-[#FFD700] text-sm mt-1">📍 {currentUser?.city}</p>
                )}
                <span className="inline-block mt-2 px-3 py-1 bg-[#22C55E]/20 border border-[#22C55E] rounded-full text-sm text-[#22C55E]">
                  Recluta
                </span>
              </CardContent>
            </Card>

            {/* Points Card */}
            <Card className="bg-[#121212] border-white/10">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-[#D946EF]" data-testid="user-balance-points">
                  {Math.round(user?.balance || 0).toLocaleString()} pts
                </p>
                <p className="text-sm text-gray-400">Puntos Disponibles</p>
              </CardContent>
            </Card>

            {/* Upcoming Launches for Reclutas - When spots are available */}
            {upcomingLaunches.filter(l => l.reclutas_can_join && !l.is_full).length > 0 && (
              <Card className="bg-gradient-to-r from-[#FF6B6B]/20 to-[#FF8E53]/20 border-[#FF6B6B]/30">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Rocket className="w-5 h-5 text-[#FF6B6B]" />
                      <h3 className="font-bold text-lg">🎉 ¡Lanzamientos Disponibles!</h3>
                    </div>
                    <span className="text-xs bg-green-500/20 text-green-400 px-2 py-1 rounded-full animate-pulse">
                      ¡Hay espacios!
                    </span>
                  </div>
                  <p className="text-xs text-gray-400 mb-3">
                    Los usuarios Pro no llenaron todos los espacios. ¡Únete y gana!
                  </p>
                  <div className="space-y-3">
                    {upcomingLaunches
                      .filter(launch => launch.reclutas_can_join && !launch.is_full)
                      .map((launch) => {
                        const submission = launchSubmissions[launch.id];
                        const hasJoined = launch.attendees?.includes(user?.id);
                        const hasSubmitted = !!submission;
                        
                        return (
                          <div 
                            key={launch.id} 
                            className={`p-3 rounded-lg ${launch.is_starting_soon ? "bg-[#FF6B6B]/30 animate-pulse" : "bg-[#1E1E1E]"}`}
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex-1">
                                <h4 className="font-bold">{launch.title}</h4>
                                <p className="text-sm text-gray-400">{launch.artist_name}</p>
                                <p className="text-xs mt-1">
                                  {launch.is_starting_soon ? (
                                    <span className="text-[#FF6B6B] font-bold animate-pulse">
                                      🔴 ¡Comienza en {launch.minutes_until_launch} minutos!
                                    </span>
                                  ) : launch.has_started ? (
                                    <span className="text-orange-400">
                                      <Clock className="w-3 h-3 inline mr-1" />
                                      ¡EN VIVO AHORA!
                                    </span>
                                  ) : (
                                    <span className="text-gray-400">
                                      <Clock className="w-3 h-3 inline mr-1" />
                                      En {launch.minutes_until_launch} minutos
                                    </span>
                                  )}
                                </p>
                                <div className="flex items-center gap-2 mt-2">
                                  <span className="text-xs px-2 py-0.5 rounded-full bg-green-500/20 text-green-400">
                                    👥 {launch.spots_available} espacios disponibles
                                  </span>
                                </div>
                              </div>
                            </div>
                            
                            {/* Submission Status */}
                            {hasSubmitted && (
                              <div className={`mt-2 p-2 rounded-lg text-sm ${
                                submission.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                                submission.status === 'approved' ? 'bg-green-500/20 text-green-400' :
                                'bg-red-500/20 text-red-400'
                              }`}>
                                {submission.status === 'pending' && '⏳ Captura en revisión'}
                                {submission.status === 'approved' && `✅ Ganaste RD$${submission.reward_amount || 0}`}
                                {submission.status === 'rejected' && '❌ Rechazada'}
                              </div>
                            )}
                            
                            {/* Join Window Warning */}
                            {launch.has_started && !launch.join_expired && !hasJoined && launch.seconds_remaining_to_join !== null && (
                              <div className="flex items-center gap-2 text-xs mt-2 bg-red-500/20 p-2 rounded-lg">
                                <AlertCircle className="w-4 h-4 text-red-500 animate-pulse" />
                                <span className="text-red-400 font-bold">
                                  ⚠️ ¡Únete ahora! Quedan {Math.floor(launch.seconds_remaining_to_join / 60)}:{(launch.seconds_remaining_to_join % 60).toString().padStart(2, '0')}
                                </span>
                              </div>
                            )}
                            
                            {/* Join Expired */}
                            {launch.join_expired && !hasJoined && (
                              <div className="text-xs mt-2 text-gray-400">
                                ❌ El tiempo para unirse expiró
                              </div>
                            )}
                            
                            {/* Upload window waiting */}
                            {hasJoined && !hasSubmitted && launch.has_started && !launch.upload_window_started && (
                              <div className="flex items-center gap-2 text-xs mt-2 text-blue-400">
                                <Clock className="w-4 h-4" />
                                {launch.seconds_until_upload_window ? (
                                  <span>⏳ Ventana de subida abre en {launch.seconds_until_upload_window} segundos</span>
                                ) : (
                                  <span>⏳ Ventana de subida abre pronto...</span>
                                )}
                              </div>
                            )}
                            
                            {/* Upload time remaining */}
                            {hasJoined && !hasSubmitted && launch.has_started && launch.upload_window_started && !launch.upload_expired && (
                              <div className="flex items-center gap-2 text-xs mt-2 text-orange-400">
                                <Clock className="w-4 h-4" />
                                ⏰ {launch.minutes_remaining_to_upload} min para subir captura
                              </div>
                            )}
                            
                            <div className="mt-3">
                              {/* Join Button - PROMINENT */}
                              {(launch.is_starting_soon || launch.has_started) && !hasJoined && !launch.join_expired && !launch.is_full && (
                                <Button
                                  onClick={() => handleJoinLaunch(launch.id)}
                                  className="w-full h-14 bg-gradient-to-r from-[#00FF88] to-[#00FFFF] text-black font-bold text-lg animate-pulse shadow-[0_0_30px_rgba(0,255,136,0.5)]"
                                  data-testid={`join-launch-${launch.id}`}
                                >
                                  <Coins className="w-6 h-6 mr-2" />
                                  🚀 ¡UNIRME AL LANZAMIENTO!
                                </Button>
                              )}
                            </div>
                            
                            {/* Upload Screenshot - only when upload window is open */}
                            {hasJoined && !hasSubmitted && launch.upload_window_started && !launch.upload_expired && (
                              <div className="mt-2 p-3 bg-[#FF6B6B]/10 rounded-lg border border-[#FF6B6B]/30">
                                <p className="text-sm text-gray-300 mb-2">
                                  📸 Sube captura para recibir tu recompensa
                                </p>
                                <input
                                  type="file"
                                  ref={el => launchScreenshotRefs.current[launch.id] = el}
                                  className="hidden"
                                  accept="image/*"
                                  onChange={(e) => {
                                    if (e.target.files[0]) {
                                      handleUploadLaunchScreenshot(launch.id, e.target.files[0]);
                                    }
                                  }}
                                />
                                <Button
                                  onClick={() => launchScreenshotRefs.current[launch.id]?.click()}
                                  disabled={uploadingLaunchScreenshot[launch.id]}
                                  className="w-full bg-[#FF6B6B] hover:bg-[#FF6B6B]/80 text-white font-bold"
                                  size="sm"
                                >
                                  {uploadingLaunchScreenshot[launch.id] ? (
                                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Subiendo...</>
                                  ) : (
                                    <><Camera className="w-4 h-4 mr-2" /> Subir Captura</>
                                  )}
                                </Button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Buzón de Envío (proof inbox) */}
            <Card className="bg-gradient-to-br from-[#8B5CF6]/20 to-[#22C55E]/10 border-[#22C55E]/30" data-testid="user-inbox-card">
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-lg">
                    <div className="p-2 rounded-lg bg-[#22C55E]/20">
                      <ClipboardList className="w-5 h-5 text-[#22C55E]" />
                    </div>
                    <span>Buzón de Envío</span>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-gray-400">
                  Sube aquí la captura de pantalla del pedido que realizaste (el link te llega por WhatsApp). El administrador la revisará y te acreditará tus puntos.
                </p>
                <Input
                  data-testid="inbox-artist-code-input"
                  value={inboxArtistCode}
                  onChange={(e) => setInboxArtistCode(e.target.value)}
                  placeholder="Código del artista (obligatorio)"
                  className="bg-[#0a0a0a] border-[#22C55E]/40"
                />

                {/* Action type selector */}
                <div>
                  <p className="text-xs text-gray-400 mb-1">¿Qué hiciste?</p>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { key: "suscriptor", label: "Suscriptor", icon: "👤", pts: 15 },
                      { key: "visita", label: "Visita", icon: "👁️", pts: 1 },
                      { key: "like_comentario", label: "Like + Comentario", icon: "👍💬", pts: 5 },
                      { key: "like", label: "Like", icon: "👍", pts: 2 },
                      { key: "comentario", label: "Comentario", icon: "💬", pts: 3 },
                    ].map(opt => (
                      <button
                        key={opt.key}
                        type="button"
                        data-testid={`inbox-type-${opt.key}`}
                        onClick={() => setInboxActionType(opt.key)}
                        className={`text-sm py-2 rounded-lg border transition-colors ${inboxActionType === opt.key ? 'bg-[#22C55E] text-black border-[#22C55E] font-bold' : 'bg-[#0a0a0a] text-gray-300 border-white/10'}`}
                      >
                        {opt.icon} {opt.label} <span className={`text-[10px] ${inboxActionType === opt.key ? 'text-black/70' : 'text-[#D946EF]'}`}>({opt.pts}pt)</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Sent counter by type */}
                <div className="grid grid-cols-3 gap-2 text-center" data-testid="inbox-counts">
                  {[
                    { key: "suscriptor", label: "Subs" },
                    { key: "visita", label: "Visitas" },
                    { key: "like_comentario", label: "Like+Com" },
                    { key: "like", label: "Likes" },
                    { key: "comentario", label: "Coment." },
                  ].map(c => (
                    <div key={c.key} className="bg-[#0a0a0a] rounded-lg py-2 border border-white/10">
                      <p className="text-lg font-bold text-[#D946EF]">{inboxCounts[c.key] || 0}</p>
                      <p className="text-[10px] text-gray-400">{c.label}</p>
                    </div>
                  ))}
                </div>

                <Input
                  data-testid="inbox-note-input"
                  value={inboxNote}
                  onChange={(e) => setInboxNote(e.target.value)}
                  placeholder="Nota (opcional): ej. me suscribí al artista X"
                  className="bg-[#0a0a0a] border-white/10"
                />
                <input
                  type="file"
                  ref={inboxFileInputRef}
                  className="hidden"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={(e) => handleInboxSubmit(e.target.files?.[0])}
                />
                <Button
                  data-testid="inbox-upload-btn"
                  onClick={() => inboxFileInputRef.current?.click()}
                  disabled={submittingInbox}
                  className="w-full bg-[#22C55E] hover:bg-[#22C55E]/80 text-black font-bold"
                >
                  {submittingInbox ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
                  Enviar {inboxActionType === 'combo' ? 'Combo' : inboxActionType === 'suscriptor' ? 'Suscripción' : inboxActionType === 'like_comentario' ? 'Like + Comentario' : inboxActionType === 'like' ? 'Like' : inboxActionType === 'comentario' ? 'Comentario' : 'Visita'}
                </Button>

                {inboxSubmissions.length > 0 && (
                  <div className="space-y-2 pt-1">
                    <p className="text-xs text-gray-400">Mis envíos ({inboxSubmissions.length})</p>
                    {inboxSubmissions.slice(0, 6).map(s => (
                      <div key={s.id} data-testid={`inbox-item-${s.id}`} className="flex items-center justify-between gap-2 bg-[#0a0a0a] rounded-lg p-2 border border-white/10">
                        <div className="flex items-center gap-2 min-w-0">
                          <img src={`${BACKEND_URL}${s.screenshot_url}`} alt="captura" className="w-10 h-10 rounded object-cover shrink-0" />
                          <span className="text-xs text-gray-300 truncate">
                            <span className="text-[#22C55E] font-bold">{s.action_type === 'like_comentario' ? 'Like + Comentario' : (s.action_type ? s.action_type.charAt(0).toUpperCase() + s.action_type.slice(1) : 'Suscriptor')}</span>
                            {s.note ? ` · ${s.note}` : ''}
                          </span>
                        </div>
                        <span className={`shrink-0 text-[11px] px-2 py-0.5 rounded-full ${
                          s.status === 'approved' ? 'bg-[#22C55E] text-black' :
                          s.status === 'rejected' ? 'bg-red-500 text-white' : 'bg-yellow-500 text-black'
                        }`}>
                          {s.status === 'approved' ? `+${s.points_awarded} pts` : s.status === 'rejected' ? 'Rechazado' : 'Pendiente'}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Assigned Tasks - PROMINENT SECTION */}
            <Card className={`border-2 ${assignedTasks.some(t => t.status === 'pending') 
              ? 'bg-gradient-to-br from-[#22C55E]/20 to-[#22C55E]/5 border-[#22C55E] animate-pulse-slow' 
              : 'bg-[#121212] border-white/10'}`}>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-lg">
                    <div className={`p-2 rounded-lg ${assignedTasks.some(t => t.status === 'pending') 
                      ? 'bg-[#22C55E] animate-bounce' 
                      : 'bg-[#22C55E]/20'}`}>
                      <ClipboardList className={`w-5 h-5 ${assignedTasks.some(t => t.status === 'pending') 
                        ? 'text-black' 
                        : 'text-[#22C55E]'}`} />
                    </div>
                    <span>Mis Tareas Asignadas</span>
                  </div>
                  {assignedTasks.filter(t => t.status === 'pending').length > 0 && (
                    <span className="px-3 py-1 bg-[#22C55E] text-black text-sm font-bold rounded-full animate-pulse">
                      {assignedTasks.filter(t => t.status === 'pending').length} PENDIENTE{assignedTasks.filter(t => t.status === 'pending').length > 1 ? 'S' : ''}
                    </span>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {assignedTasks.length === 0 ? (
                  <div className="text-center py-6">
                    <ClipboardList className="w-12 h-12 text-gray-500 mx-auto mb-3" />
                    <p className="text-gray-400">No tienes tareas asignadas</p>
                    <p className="text-sm text-gray-500">El administrador te asignará tareas pronto</p>
                  </div>
                ) : (
                  assignedTasks.map((task) => (
                    <div key={task.id} className={`rounded-lg p-4 space-y-3 ${
                      task.status === 'pending' 
                        ? 'bg-[#22C55E]/10 border-2 border-[#22C55E]/50' 
                        : 'bg-[#1E1E1E]'
                    }`}>
                      <div className="flex items-start justify-between">
                        <div>
                          <h4 className="font-bold text-white">{task.title}</h4>
                          <p className="text-sm text-gray-400">{task.description}</p>
                          {/* Fecha y hora de la tarea */}
                          {task.created_at && (
                            <p className="text-xs text-[#FFD700] mt-1 flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              Asignada: {new Date(task.created_at).toLocaleString('es-DO', { 
                                dateStyle: 'short', 
                                timeStyle: 'short' 
                              })}
                            </p>
                          )}
                        </div>
                        <span className={`px-2 py-1 rounded text-xs font-bold ${
                          task.status === 'pending' ? 'bg-yellow-500 text-black' :
                          task.status === 'completed' ? 'bg-blue-500/20 text-blue-500' :
                          task.status === 'approved' ? 'bg-green-500/20 text-green-500' :
                          'bg-red-500/20 text-red-500'
                        }`}>
                          {task.status === 'pending' ? '⚡ PENDIENTE' :
                           task.status === 'completed' ? 'En revisión' :
                           task.status === 'approved' ? '✓ Aprobada' : 'Rechazada'}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-[#22C55E] font-bold text-lg">💰 RD${task.reward}</span>
                        {task.task_url && (
                          <a href={task.task_url} target="_blank" rel="noopener noreferrer" 
                             className="px-3 py-1 bg-[#D946EF] text-white rounded-full flex items-center gap-1 text-xs font-bold hover:bg-[#D946EF]/80">
                            <ExternalLink className="w-3 h-3" /> IR A LA TAREA
                          </a>
                        )}
                      </div>
                      {task.status === 'pending' && (
                        <div className="pt-2">
                          <input
                            type="file"
                            id={`task-file-${task.id}`}
                            className="hidden"
                            accept="image/*"
                            onChange={(e) => {
                              if (e.target.files[0]) {
                                handleCompleteAssignedTask(task.id, e.target.files[0]);
                              }
                            }}
                          />
                          <Button
                            onClick={() => document.getElementById(`task-file-${task.id}`).click()}
                            disabled={uploadingTaskScreenshot[task.id]}
                            size="lg"
                            className="w-full bg-[#22C55E] hover:bg-[#22C55E]/80 text-black font-bold text-base py-6"
                          >
                            {uploadingTaskScreenshot[task.id] ? (
                              <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Subiendo...</>
                            ) : (
                              <><Camera className="w-5 h-5 mr-2" /> 📸 SUBIR CAPTURA Y GANAR</>
                            )}
                          </Button>
                        </div>
                      )}
                      {task.screenshot_url && (
                        <div className="pt-2">
                          <img 
                            src={`${BACKEND_URL}${task.screenshot_url}`} 
                            alt="Captura" 
                            className="w-full h-32 object-cover rounded-lg border border-white/10"
                          />
                        </div>
                      )}
                    </div>
                  ))
                )}
              </CardContent>
            </Card>

            {/* Daily Visits Section */}
            {dailyVisits.length > 0 && (
              <Card className={`border-2 ${dailyVisits.some(v => v.can_submit && v.spots_remaining > 0) 
                ? 'bg-gradient-to-br from-[#FF6B6B]/20 to-[#FF8E53]/5 border-[#FF6B6B]' 
                : 'bg-[#121212] border-white/10'}`}>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-lg">
                      <div className={`p-2 rounded-lg ${dailyVisits.some(v => v.can_submit) 
                        ? 'bg-[#FF6B6B] animate-bounce' 
                        : 'bg-[#FF6B6B]/20'}`}>
                        <Eye className={`w-5 h-5 ${dailyVisits.some(v => v.can_submit) 
                          ? 'text-white' 
                          : 'text-[#FF6B6B]'}`} />
                      </div>
                      <span>Visitas Diarias</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-1 bg-green-500/20 text-green-500 text-xs rounded">
                        ✓ {dailyVisitsStats.total_completed_today} completadas
                      </span>
                      {dailyVisitsStats.total_pending > 0 && (
                        <span className="px-2 py-1 bg-yellow-500/20 text-yellow-500 text-xs rounded">
                          ⏳ {dailyVisitsStats.total_pending} pendientes
                        </span>
                      )}
                      <span className="px-2 py-1 bg-blue-500/20 text-blue-500 text-xs rounded">
                        📊 {dailyVisitsStats.total_available || dailyVisits.filter(v => v.can_submit).length} disponibles
                      </span>
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {dailyVisits.map((visit) => (
                    <div key={visit.id} className={`rounded-lg p-4 space-y-3 ${
                      visit.can_submit && visit.spots_remaining > 0
                        ? 'bg-[#FF6B6B]/10 border-2 border-[#FF6B6B]/50' 
                        : 'bg-[#1E1E1E]'
                    }`}>
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          {getPlatformIcon(visit.platform)}
                          <div>
                            <h4 className="font-bold text-white">{visit.title}</h4>
                            <p className="text-sm text-gray-400 capitalize">{visit.platform}</p>
                            {/* Fecha y hora de la tarea */}
                            {visit.created_at && (
                              <p className="text-xs text-[#FFD700] flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {new Date(visit.created_at).toLocaleString('es-DO', { 
                                  dateStyle: 'short', 
                                  timeStyle: 'short' 
                                })}
                              </p>
                            )}
                          </div>
                        </div>
                        {visit.user_pending_count > 0 && (
                          <span className="px-2 py-1 bg-yellow-500 text-black text-xs font-bold rounded">
                            ⏳ {visit.user_pending_count} en revisión
                          </span>
                        )}
                      </div>
                      
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-[#FF6B6B] font-bold text-lg">💰 RD${visit.reward_amount}</span>
                        
                        {/* Timer Display */}
                        {visitTimers[visit.id]?.started && (
                          <div className={`px-3 py-1 rounded-full text-sm font-bold ${
                            visitTimers[visit.id]?.canSubmit 
                              ? 'bg-green-500/20 text-green-500' 
                              : 'bg-yellow-500/20 text-yellow-500 animate-pulse'
                          }`}>
                            ⏱️ {visitTimers[visit.id]?.elapsedFormatted || '0:00'}
                            {!visitTimers[visit.id]?.canSubmit && ' (mín 1:20)'}
                          </div>
                        )}
                        
                        {/* Start Visit Button - Always available */}
                        {!visitTimers[visit.id]?.started && (
                          <button 
                            onClick={() => handleStartVisit(visit.id, visit.target_url)}
                            className="px-3 py-1 bg-gradient-to-r from-[#FF6B6B] to-[#FF8E53] text-white rounded-full flex items-center gap-1 text-xs font-bold hover:opacity-80"
                          >
                            <ExternalLink className="w-3 h-3" /> IR A VISITAR
                          </button>
                        )}
                      </div>
                      
                      {/* Timer Message */}
                      {visitTimers[visit.id]?.started && !visitTimers[visit.id]?.canSubmit && (
                        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-2 text-center mt-2">
                          <p className="text-yellow-500 text-xs">⏳ Espera al menos 1:20 en la visita para poder subir la captura</p>
                        </div>
                      )}
                      
                      {/* Upload Button - Show when timer allows */}
                      {visitTimers[visit.id]?.canSubmit && (
                        <div className="pt-2">
                          <input
                            type="file"
                            id={`visit-file-${visit.id}`}
                            className="hidden"
                            accept="image/*"
                            onChange={(e) => {
                              if (e.target.files[0]) {
                                handleCompleteDailyVisit(visit.id, e.target.files[0]);
                              }
                            }}
                          />
                          <Button
                            onClick={() => document.getElementById(`visit-file-${visit.id}`).click()}
                            disabled={uploadingVisitScreenshot[visit.id]}
                            size="lg"
                            className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:opacity-80 text-white font-bold text-base py-6"
                          >
                            {uploadingVisitScreenshot[visit.id] ? (
                              <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Subiendo...</>
                            ) : (
                              <><Camera className="w-5 h-5 mr-2" /> ✅ SUBIR CAPTURA ({visitTimers[visit.id]?.elapsedFormatted})</>
                            )}
                          </Button>
                        </div>
                      )}
                      
                      {/* Show message if timer started but can't submit yet */}
                      {visit.can_submit && visit.spots_remaining > 0 && visitTimers[visit.id]?.started && !visitTimers[visit.id]?.canSubmit && (
                        <div className="pt-2">
                          <Button
                            disabled
                            size="lg"
                            className="w-full bg-gray-600 text-white font-bold text-base py-6 cursor-not-allowed"
                          >
                            <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Esperando tiempo mínimo...
                          </Button>
                        </div>
                      )}
                      
                      {visit.user_submission?.screenshot_url && (
                        <div className="pt-2">
                          <img 
                            src={`${BACKEND_URL}${visit.user_submission.screenshot_url}`} 
                            alt="Captura" 
                            className="w-full h-32 object-cover rounded-lg border border-white/10"
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Bank Info for Recluta */}
            <Card className="bg-[#121212] border-white/10">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-[#D946EF]" />
                  Información Bancaria
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {user?.bank_name && user?.bank_account ? (
                  <>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Titular:</span>
                      <span>{user.bank_holder || user.name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Banco:</span>
                      <span>{user.bank_name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Tipo:</span>
                      <span className="capitalize">{user.account_type || "Ahorro"}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Cuenta:</span>
                      <span className="mono">{user.bank_account}</span>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full mt-2 border-[#D946EF]/50 text-[#D946EF]"
                      onClick={() => {
                        setBankForm({ 
                          bank_name: user.bank_name || "", 
                          bank_account: user.bank_account || "",
                          bank_holder: user.bank_holder || "",
                          account_type: user.account_type || "ahorro"
                        });
                        setShowBankForm(true);
                      }}
                    >
                      Cambiar Datos Bancarios
                    </Button>
                  </>
                ) : (
                  <div className="text-center py-2">
                    <p className="text-sm text-gray-400 mb-3">No tienes datos bancarios registrados</p>
                    <Button
                      className="bg-[#D946EF] hover:bg-[#D946EF]/80 text-white"
                      onClick={() => {
                        setBankForm({ bank_name: "", bank_account: "", bank_holder: "", account_type: "ahorro" });
                        setShowBankForm(true);
                      }}
                    >
                      Agregar Datos Bancarios
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Upgrade to Pro */}
            <Card className="bg-gradient-to-br from-[#D946EF]/10 to-[#8B5CF6]/10 border-[#D946EF]/30">
              <CardContent className="p-4">
                <div className="flex items-center gap-3 mb-3">
                  <Crown className="w-6 h-6 text-[#D946EF]" />
                  <div>
                    <h4 className="font-bold text-white">¿Quieres acceso completo?</h4>
                    <p className="text-sm text-gray-400">Actualiza a Pro por RD$1,100</p>
                  </div>
                </div>
                <Button
                  onClick={handleRequestProUpgrade}
                  className="w-full bg-[#D946EF] hover:bg-[#D946EF]/80"
                >
                  <Star className="w-4 h-4 mr-2" /> Actualizar a Pro
                </Button>
              </CardContent>
            </Card>

            {/* Logout Button */}
            <Button
              onClick={handleLogout}
              variant="outline"
              className="w-full border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Cerrar Sesión
            </Button>
          </div>

          {/* Bank Form Modal for Recluta */}
          {showBankForm && (
            <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
              <Card className="bg-[#121212] border-white/10 w-full max-w-md">
                <CardHeader>
                  <CardTitle>Datos Bancarios</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Nombre del Titular *</Label>
                    <Input
                      value={bankForm.bank_holder}
                      onChange={(e) => setBankForm({ ...bankForm, bank_holder: e.target.value })}
                      className="bg-[#1E1E1E] border-white/10"
                      placeholder="Nombre completo del titular"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Banco *</Label>
                    <Select
                      value={bankForm.bank_name}
                      onValueChange={(v) => setBankForm({ ...bankForm, bank_name: v })}
                    >
                      <SelectTrigger className="bg-[#1E1E1E] border-white/10">
                        <SelectValue placeholder="Seleccionar banco" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1E1E1E] border-white/10">
                        <SelectItem value="Banco Popular">Banco Popular</SelectItem>
                        <SelectItem value="Banreservas">Banreservas</SelectItem>
                        <SelectItem value="BHD León">BHD León</SelectItem>
                        <SelectItem value="Scotiabank">Scotiabank</SelectItem>
                        <SelectItem value="Banco Santa Cruz">Banco Santa Cruz</SelectItem>
                        <SelectItem value="Asociación Popular">Asociación Popular</SelectItem>
                        <SelectItem value="Otro">Otro</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Tipo de Cuenta *</Label>
                    <Select
                      value={bankForm.account_type}
                      onValueChange={(v) => setBankForm({ ...bankForm, account_type: v })}
                    >
                      <SelectTrigger className="bg-[#1E1E1E] border-white/10">
                        <SelectValue placeholder="Seleccionar tipo" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1E1E1E] border-white/10">
                        <SelectItem value="ahorro">Cuenta de Ahorro</SelectItem>
                        <SelectItem value="corriente">Cuenta Corriente</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Número de Cuenta *</Label>
                    <Input
                      value={bankForm.bank_account}
                      onChange={(e) => setBankForm({ ...bankForm, bank_account: e.target.value })}
                      className="bg-[#1E1E1E] border-white/10"
                      placeholder="Número de cuenta"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={() => setShowBankForm(false)}
                    >
                      Cancelar
                    </Button>
                    <Button
                      className="flex-1 bg-[#D946EF] hover:bg-[#D946EF]/80"
                      onClick={handleSaveBankInfo}
                      disabled={savingBank}
                    >
                      {savingBank ? "Guardando..." : "Guardar"}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      );
    }
    
    // Show subscription payment UI for non-subscribed PRO users
    if (!isAdmin && !isSubscribed) {
      return (
        <div className="min-h-screen pb-20 p-4 animate-fadeInUp" data-testid="user-subscription">
          <div className="max-w-md mx-auto space-y-6">
            {/* User Header */}
            <Card className="bg-gradient-to-br from-[#D946EF]/20 to-[#8B5CF6]/20 border-white/10">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 rounded-full bg-[#D946EF] flex items-center justify-center mx-auto mb-4">
                  <User className="w-8 h-8 text-white" />
                </div>
                <h2 className="text-xl font-bold">{currentUser?.name} {currentUser?.last_name}</h2>
                <p className="text-gray-400">{currentUser?.phone}</p>
                {currentUser?.city && (
                  <p className="text-[#FFD700] text-sm mt-1">📍 {currentUser?.city}</p>
                )}
                {userTier === "pro" && (
                  <span className="inline-block mt-2 px-3 py-1 bg-[#D946EF]/20 border border-[#D946EF] rounded-full text-sm text-[#D946EF]">
                    Pro (Pendiente Pago)
                  </span>
                )}
              </CardContent>
            </Card>

            {/* Subscription Status Card */}
            <Card className="bg-[#121212] border-white/10">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Banknote className="w-5 h-5 text-[#22C55E]" />
                  Inscripción Requerida
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {subStatus === "confirming" ? (
                  // Payment being verified
                  <div className="text-center py-4">
                    <Clock className="w-12 h-12 text-yellow-500 mx-auto mb-3" />
                    <h3 className="text-lg font-bold text-yellow-500">Verificando Pago</h3>
                    <p className="text-gray-400 mt-2">
                      Tu comprobante está siendo revisado por el administrador.
                    </p>
                    <p className="text-sm text-gray-500 mt-2">
                      Referencia: {subscriptionStatus?.payment?.reference}
                    </p>
                  </div>
                ) : showPaymentForm || subscriptionStatus?.payment?.status === "pending" ? (
                  // Payment form
                  <div className="space-y-4">
                    <div className="bg-[#1E1E1E] rounded-lg p-4">
                      <p className="text-sm text-gray-400 mb-1">Monto a Pagar</p>
                      <p className="text-3xl font-bold text-[#22C55E]">
                        RD$ {subscriptionInfo?.fee?.toLocaleString() || "1,100"}
                      </p>
                    </div>

                    {/* Bank Account Info */}
                    {subscriptionInfo?.payment_account && (
                      <div className="bg-[#1E1E1E] rounded-lg p-4 space-y-2">
                        <p className="font-bold text-[#D946EF] mb-3">Datos para Transferencia</p>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-400">Banco:</span>
                          <span>{subscriptionInfo.payment_account.bank}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-400">Cuenta:</span>
                          <div className="flex items-center gap-2">
                            <span className="mono">{subscriptionInfo.payment_account.account}</span>
                            <button
                              onClick={() => {
                                navigator.clipboard.writeText(subscriptionInfo.payment_account.account);
                                toast.success("Número de cuenta copiado");
                              }}
                              className="text-[#D946EF] hover:text-[#D946EF]/80"
                            >
                              <Copy className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-400">Titular:</span>
                          <span>{subscriptionInfo.payment_account.name}</span>
                        </div>
                        {subscriptionInfo.payment_account.rnc && (
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-400">RNC:</span>
                            <span>{subscriptionInfo.payment_account.rnc}</span>
                          </div>
                        )}
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-400">Tipo:</span>
                          <span>{subscriptionInfo.payment_account.type}</span>
                        </div>
                      </div>
                    )}

                    {/* Upload Receipt */}
                    <div className="space-y-3">
                      <Label className="flex items-center gap-2">
                        <Upload className="w-4 h-4" />
                        Subir Comprobante / Voucher
                      </Label>
                      
                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileChange}
                        accept="image/jpeg,image/png,image/jpg,image/webp"
                        className="hidden"
                      />
                      
                      {receiptPreview ? (
                        <div className="relative">
                          <img 
                            src={receiptPreview} 
                            alt="Comprobante" 
                            className="w-full h-48 object-cover rounded-lg border border-white/10"
                          />
                          <button
                            onClick={() => {
                              setReceiptFile(null);
                              setReceiptPreview(null);
                            }}
                            className="absolute top-2 right-2 p-1 bg-red-500 rounded-full"
                          >
                            <X className="w-4 h-4 text-white" />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => fileInputRef.current?.click()}
                          className="w-full h-32 border-2 border-dashed border-white/20 rounded-lg flex flex-col items-center justify-center gap-2 hover:border-[#D946EF] transition-colors"
                        >
                          <Image className="w-8 h-8 text-gray-400" />
                          <span className="text-sm text-gray-400">Toca para seleccionar imagen</span>
                          <span className="text-xs text-gray-500">JPG, PNG o WEBP (máx 5MB)</span>
                        </button>
                      )}
                    </div>

                    <Button
                      onClick={handleUploadReceipt}
                      disabled={loading || !receiptFile}
                      className="w-full bg-[#22C55E] hover:bg-[#22C55E]/80 text-black h-12 font-bold"
                    >
                      {loading ? "Subiendo..." : "Enviar Comprobante"}
                    </Button>
                  </div>
                ) : (
                  // Initial subscription request
                  <div className="space-y-4">
                    <div className="flex items-start gap-3 p-4 bg-yellow-500/10 rounded-lg border border-yellow-500/20">
                      <AlertCircle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm">
                          Para comenzar a ganar dinero viendo videos, debes completar tu inscripción pagando una tarifa única de <strong className="text-[#22C55E]">RD$ 1,100</strong>.
                        </p>
                      </div>
                    </div>

                    <Button
                      onClick={handleRequestSubscription}
                      disabled={loading}
                      className="w-full bg-[#D946EF] hover:bg-[#D946EF]/80 h-12 font-bold"
                    >
                      {loading ? "Cargando..." : "Pagar Inscripción"}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Logout Button */}
            <Button
              onClick={handleLogout}
              variant="outline"
              className="w-full border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Cerrar Sesión
            </Button>
          </div>
        </div>
      );
    }
    
    return (
      <div className="min-h-screen pb-20 p-4 animate-fadeInUp" data-testid="user-profile">
        <div className="max-w-md mx-auto space-y-6">
          {/* Profile Header */}
          <Card className="bg-gradient-to-br from-[#D946EF]/20 to-[#8B5CF6]/20 border-white/10">
            <CardContent className="p-6 text-center">
              {/* Profile Photo with Crown for Pro users */}
              <div className="relative inline-block mb-4">
                {/* Crown for Pro users */}
                {!isAdmin && (user?.user_tier === "pro" || user?.pro_status === "approved") && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-10">
                    <Crown className="w-8 h-8 text-[#FFD700] drop-shadow-[0_0_10px_rgba(255,215,0,0.8)]" />
                  </div>
                )}
                
                {/* Photo container */}
                <div className="relative group">
                  <div className={`w-20 h-20 rounded-full flex items-center justify-center overflow-hidden border-4 ${
                    !isAdmin && (user?.user_tier === "pro" || user?.pro_status === "approved")
                      ? 'border-[#FFD700] shadow-[0_0_20px_rgba(255,215,0,0.5)]'
                      : 'border-[#D946EF]'
                  } bg-[#D946EF]`}>
                    {isAdmin ? (
                      <Shield className="w-10 h-10 text-white" />
                    ) : user?.photo_url ? (
                      <img 
                        src={`${BACKEND_URL}${user.photo_url}`} 
                        alt={user.name} 
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <User className="w-10 h-10 text-white" />
                    )}
                  </div>
                  
                  {/* Upload overlay for Pro users */}
                  {!isAdmin && (user?.user_tier === "pro" || user?.pro_status === "approved") && (
                    <>
                      <input
                        type="file"
                        ref={userPhotoInputRef}
                        className="hidden"
                        accept="image/jpeg,image/png,image/webp"
                        onChange={handleUploadUserPhoto}
                      />
                      <button
                        onClick={() => userPhotoInputRef.current?.click()}
                        disabled={uploadingPhoto}
                        className="absolute inset-0 flex items-center justify-center bg-black/60 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                      >
                        {uploadingPhoto ? (
                          <Loader2 className="w-6 h-6 text-white animate-spin" />
                        ) : (
                          <Camera className="w-6 h-6 text-white" />
                        )}
                      </button>
                    </>
                  )}
                </div>
                
                {/* Pro badge hint */}
                {!isAdmin && (user?.user_tier === "pro" || user?.pro_status === "approved") && (
                  <p className="text-[10px] text-[#FFD700] mt-1">Toca para cambiar foto</p>
                )}
              </div>
              
              <h2 className="text-2xl font-bold">
                {isAdmin ? currentUser?.name : `${currentUser?.name} ${currentUser?.last_name}`}
              </h2>
              <p className="text-gray-400">{currentUser?.phone}</p>
              {currentUser?.city && !isAdmin && (
                <p className="text-[#FFD700] text-sm mt-1">📍 {currentUser?.city}</p>
              )}
              {isAdmin && (
                <span className="inline-block mt-2 px-3 py-1 bg-[#8B5CF6] rounded-full text-sm">
                  Administrador
                </span>
              )}
              {!isAdmin && (user?.user_tier === "pro" || user?.pro_status === "approved") && (
                <span className="inline-flex items-center gap-1 mt-2 px-4 py-1 bg-gradient-to-r from-[#FFD700] to-[#B8860B] rounded-full text-sm font-bold text-black">
                  <Crown className="w-4 h-4" /> USUARIO PRO
                </span>
              )}
            </CardContent>
          </Card>

          {/* Admin Panel Button */}
          {isAdmin && (
            <Button
              onClick={() => navigate("/admin")}
              data-testid="admin-panel-btn"
              className="w-full bg-gradient-to-r from-[#8B5CF6] to-[#D946EF] hover:from-[#7C3AED] hover:to-[#C026D3] rounded-xl h-14 text-lg font-bold shadow-lg"
            >
              <Settings className="w-6 h-6 mr-3" />
              Panel de Administrador
            </Button>
          )}

          {/* User Stats (only for regular users) */}
          {!isAdmin && user && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <Card className="bg-[#121212] border-white/10">
                  <CardContent className="p-4 text-center">
                    <p className="text-2xl font-bold text-[#D946EF]">
                      {Math.round(user.balance || 0).toLocaleString()} pts
                    </p>
                    <p className="text-sm text-gray-400">Puntos Actuales</p>
                  </CardContent>
                </Card>
                <Card className="bg-[#121212] border-white/10">
                  <CardContent className="p-4 text-center">
                    <p className="text-2xl font-bold text-[#D946EF]">
                      {user.total_views || 0}
                    </p>
                    <p className="text-sm text-gray-400">Vistas Totales</p>
                  </CardContent>
                </Card>
              </div>

              {/* Bank Info */}
              <Card className="bg-[#121212] border-white/10">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-[#D946EF]" />
                    Información Bancaria
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {user.bank_name && user.bank_account ? (
                    <>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Titular:</span>
                        <span>{user.bank_holder || user.name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Banco:</span>
                        <span>{user.bank_name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Tipo:</span>
                        <span className="capitalize">{user.account_type || "Ahorro"}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Cuenta:</span>
                        <span className="mono">{user.bank_account}</span>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full mt-2 border-[#D946EF]/50 text-[#D946EF]"
                        onClick={() => {
                          setBankForm({ 
                            bank_name: user.bank_name || "", 
                            bank_account: user.bank_account || "",
                            bank_holder: user.bank_holder || "",
                            account_type: user.account_type || "ahorro"
                          });
                          setShowBankForm(true);
                        }}
                      >
                        Cambiar Datos Bancarios
                      </Button>
                    </>
                  ) : (
                    <div className="text-center py-2">
                      <p className="text-sm text-gray-400 mb-3">No tienes datos bancarios registrados</p>
                      <Button
                        className="bg-[#D946EF] hover:bg-[#D946EF]/80 text-white"
                        onClick={() => {
                          setBankForm({ bank_name: "", bank_account: "", bank_holder: "", account_type: "ahorro" });
                          setShowBankForm(true);
                        }}
                      >
                        Agregar Datos Bancarios
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Bank Form Modal */}
              {showBankForm && (
                <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
                  <Card className="bg-[#121212] border-white/10 w-full max-w-md">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Building2 className="w-5 h-5 text-[#D946EF]" />
                        Datos Bancarios
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="space-y-2">
                        <Label>Nombre del Titular *</Label>
                        <Input
                          value={bankForm.bank_holder}
                          onChange={(e) => setBankForm({ ...bankForm, bank_holder: e.target.value })}
                          className="bg-[#1E1E1E] border-white/10"
                          placeholder="Nombre completo del titular"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Banco *</Label>
                        <Select
                          value={bankForm.bank_name}
                          onValueChange={(v) => setBankForm({ ...bankForm, bank_name: v })}
                        >
                          <SelectTrigger className="bg-[#1E1E1E] border-white/10">
                            <SelectValue placeholder="Selecciona tu banco" />
                          </SelectTrigger>
                          <SelectContent className="bg-[#1E1E1E] border-white/10">
                            <SelectItem value="Banreservas">Banreservas</SelectItem>
                            <SelectItem value="BHD León">BHD León</SelectItem>
                            <SelectItem value="Banco Popular">Banco Popular</SelectItem>
                            <SelectItem value="Banco Santa Cruz">Banco Santa Cruz</SelectItem>
                            <SelectItem value="Scotiabank">Scotiabank</SelectItem>
                            <SelectItem value="Asociación Popular">Asociación Popular</SelectItem>
                            <SelectItem value="Otro">Otro</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Tipo de Cuenta *</Label>
                        <Select
                          value={bankForm.account_type}
                          onValueChange={(v) => setBankForm({ ...bankForm, account_type: v })}
                        >
                          <SelectTrigger className="bg-[#1E1E1E] border-white/10">
                            <SelectValue placeholder="Seleccionar tipo" />
                          </SelectTrigger>
                          <SelectContent className="bg-[#1E1E1E] border-white/10">
                            <SelectItem value="ahorro">Cuenta de Ahorro</SelectItem>
                            <SelectItem value="corriente">Cuenta Corriente</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Número de Cuenta *</Label>
                        <Input
                          placeholder="1234567890"
                          className="bg-[#1E1E1E] border-white/10"
                          value={bankForm.bank_account}
                          onChange={(e) => setBankForm({ ...bankForm, bank_account: e.target.value })}
                        />
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          className="flex-1 border-white/20"
                          onClick={() => setShowBankForm(false)}
                        >
                          Cancelar
                        </Button>
                        <Button
                          className="flex-1 bg-[#D946EF] hover:bg-[#D946EF]/80"
                          onClick={handleSaveBankInfo}
                          disabled={savingBank}
                        >
                          {savingBank ? "Guardando..." : "Guardar"}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Referral Code */}
              <Card className="bg-[#121212] border-white/10">
                <CardContent className="p-4">
                  <p className="text-sm text-gray-400 mb-2">Tu Código de Referido</p>
                  <div className="flex items-center justify-between bg-[#1E1E1E] rounded-lg p-3">
                    <span className="mono text-lg text-[#D946EF]">{user.referral_code}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        navigator.clipboard.writeText(user.referral_code);
                        toast.success("Código copiado");
                      }}
                    >
                      Copiar
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Upcoming Launches - PRO USERS + RECLUTAS when spots available */}
              {upcomingLaunches.length > 0 && (
                upcomingLaunches.some(l => 
                  (user?.user_tier === "pro" || user?.tier === "pro") || 
                  (l.reclutas_can_join && !l.is_full)
                )
              ) && (
                <Card className="bg-gradient-to-r from-[#FF6B6B]/20 to-[#FF8E53]/20 border-[#FF6B6B]/30">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Rocket className="w-5 h-5 text-[#FF6B6B]" />
                        <h3 className="font-bold text-lg">Lanzamientos Próximos</h3>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={fetchUpcomingLaunches}
                          className="text-xs text-gray-400 hover:text-[#FF6B6B] p-1 h-auto"
                          data-testid="refresh-launches-btn"
                        >
                          🔄
                        </Button>
                      </div>
                      {user?.user_tier !== "pro" && user?.tier !== "pro" && (
                        <span className="text-xs bg-green-500/20 text-green-400 px-2 py-1 rounded-full">
                          ¡Espacios disponibles!
                        </span>
                      )}
                    </div>
                    <div className="space-y-3">
                      {upcomingLaunches
                        .filter(launch => 
                          (user?.user_tier === "pro" || user?.tier === "pro") || 
                          (launch.reclutas_can_join && !launch.is_full)
                        )
                        .map((launch) => {
                        const submission = launchSubmissions[launch.id];
                        const hasJoined = launch.attendees?.includes(user?.id);
                        const hasSubmitted = !!submission;
                        
                        return (
                          <div 
                            key={launch.id} 
                            className={`p-3 rounded-lg ${launch.is_starting_soon ? "bg-[#FF6B6B]/30 animate-pulse" : "bg-[#1E1E1E]"}`}
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex-1">
                                <h4 className="font-bold">{launch.title}</h4>
                                <p className="text-sm text-gray-400">{launch.artist_name}</p>
                                <p className="text-xs mt-1">
                                  {launch.is_starting_soon ? (
                                    <span className="text-[#FF6B6B] font-bold animate-pulse">
                                      🔴 ¡Comienza en {launch.minutes_until_launch} minutos!
                                    </span>
                                  ) : launch.has_started ? (
                                    <span className="text-orange-400">
                                      <Clock className="w-3 h-3 inline mr-1" />
                                      ¡EN VIVO AHORA!
                                    </span>
                                  ) : (
                                    <span className="text-gray-400">
                                      <Clock className="w-3 h-3 inline mr-1" />
                                      En {launch.minutes_until_launch} minutos
                                    </span>
                                  )}
                                </p>
                                {launch.description && (
                                  <p className="text-xs text-gray-500 mt-1 line-clamp-1">{launch.description}</p>
                                )}
                                {/* Spots available info */}
                                <div className="flex items-center gap-2 mt-2">
                                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                                    launch.is_full 
                                      ? 'bg-red-500/20 text-red-400' 
                                      : launch.spots_available <= 10 
                                        ? 'bg-yellow-500/20 text-yellow-400' 
                                        : 'bg-green-500/20 text-green-400'
                                  }`}>
                                    {launch.is_full 
                                      ? '🚫 Lleno' 
                                      : `👥 ${launch.spots_available}/${launch.max_participants} espacios`
                                    }
                                  </span>
                                  {launch.pro_participants > 0 && (
                                    <span className="text-xs text-purple-400">
                                      {launch.pro_participants} Pro
                                    </span>
                                  )}
                                </div>
                              </div>
                              {launch.thumbnail_url && (
                                <img 
                                  src={launch.thumbnail_url} 
                                  alt={launch.title}
                                  className="w-20 h-12 object-cover rounded"
                                />
                              )}
                            </div>
                            
                            {/* Submission Status */}
                            {hasSubmitted && (
                              <div className={`mt-2 p-2 rounded-lg text-sm ${
                                submission.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                                submission.status === 'approved' ? 'bg-green-500/20 text-green-400' :
                                'bg-red-500/20 text-red-400'
                              }`}>
                                {submission.status === 'pending' && '⏳ Captura en revisión por el admin'}
                                {submission.status === 'approved' && `✅ Aprobado - Ganaste RD$${submission.reward_amount || 0}`}
                                {submission.status === 'rejected' && '❌ Captura rechazada'}
                              </div>
                            )}
                            
                            {/* Join Window Expiring Warning */}
                            {launch.has_started && !launch.join_expired && !hasJoined && launch.seconds_remaining_to_join !== null && (
                              <div className="flex items-center gap-2 text-xs mt-2 bg-red-500/20 p-2 rounded-lg">
                                <AlertCircle className="w-4 h-4 text-red-500 animate-pulse" />
                                <span className="text-red-400 font-bold animate-pulse">
                                  ⚠️ ¡Únete ahora! Quedan {Math.floor(launch.seconds_remaining_to_join / 60)}:{(launch.seconds_remaining_to_join % 60).toString().padStart(2, '0')} para unirte
                                </span>
                              </div>
                            )}
                            
                            {/* Join Window Expired */}
                            {launch.join_expired && !hasJoined && (
                              <div className="flex items-center gap-2 text-xs mt-2 bg-gray-500/20 p-2 rounded-lg">
                                <AlertCircle className="w-4 h-4 text-gray-500" />
                                <span className="text-gray-400 font-bold">
                                  ❌ El tiempo para unirse expiró (2 min después del lanzamiento)
                                </span>
                              </div>
                            )}
                            
                            {/* Upload window not started yet - waiting for join window to close */}
                            {hasJoined && !hasSubmitted && launch.has_started && !launch.upload_window_started && (
                              <div className="flex items-center gap-2 text-xs mt-2 bg-blue-500/20 p-2 rounded-lg">
                                <Clock className="w-4 h-4 text-blue-400" />
                                <span className="text-blue-400">
                                  {launch.seconds_until_upload_window ? (
                                    <>⏳ Ventana de subida abre en {launch.seconds_until_upload_window} segundos</>
                                  ) : (
                                    <>⏳ Ventana de subida abre cuando cierre la ventana de unirse</>
                                  )}
                                </span>
                              </div>
                            )}
                            
                            {/* Time remaining indicator for screenshot upload */}
                            {hasJoined && !hasSubmitted && launch.has_started && launch.upload_window_started && launch.minutes_remaining_to_upload !== null && !launch.upload_expired && (
                              <div className="flex items-center gap-2 text-xs mt-2">
                                <AlertCircle className="w-4 h-4 text-orange-500" />
                                <span className="text-orange-400 font-bold animate-pulse">
                                  ⏰ Tiempo restante: {launch.minutes_remaining_to_upload} min para subir captura
                                </span>
                              </div>
                            )}
                            
                            {/* Upload Window Expired */}
                            {hasJoined && !hasSubmitted && launch.upload_expired && (
                              <div className="flex items-center gap-2 text-xs mt-2 bg-red-500/20 p-2 rounded-lg">
                                <AlertCircle className="w-4 h-4 text-red-500" />
                                <span className="text-red-400 font-bold">
                                  ❌ El tiempo para subir captura expiró
                                </span>
                              </div>
                            )}
                            
                            <div className="mt-3 flex flex-col gap-2">
                              {/* Join Button - PROMINENT */}
                              {(launch.is_starting_soon || launch.has_started) && !hasJoined && !launch.join_expired && (
                                <Button
                                  onClick={() => handleJoinLaunch(launch.id)}
                                  className="w-full h-14 bg-gradient-to-r from-[#00FF88] to-[#00FFFF] text-black font-bold text-lg animate-pulse shadow-[0_0_30px_rgba(0,255,136,0.5)]"
                                  data-testid={`join-launch-pro-${launch.id}`}
                                >
                                  <Coins className="w-6 h-6 mr-2" />
                                  🚀 ¡UNIRME AL LANZAMIENTO!
                                </Button>
                              )}
                              
                              {/* Other action buttons */}
                              <div className="flex flex-wrap gap-2 items-center">
                                {/* Confirm Button - before launch */}
                                {launch.is_confirmable && !launch.is_starting_soon && (
                                  <Button
                                    onClick={() => handleConfirmAttendance(launch.id)}
                                    size="sm"
                                    className="bg-gradient-to-r from-[#FFD700] to-[#B8860B] hover:from-[#B8860B] hover:to-[#FFD700] text-black font-bold"
                                    disabled={launch.confirmed_attendees?.includes(user?.id)}
                                  >
                                    <CheckCircle className="w-4 h-4 mr-1" />
                                    {launch.confirmed_attendees?.includes(user?.id) ? "Confirmado" : "Confirmar"}
                                  </Button>
                                )}
                                
                                {/* Not available yet */}
                                {!launch.is_starting_soon && !launch.has_started && !launch.task_expired && (
                                  <span className="text-xs text-gray-400">
                                    El botón aparecerá cuando comience el lanzamiento
                                  </span>
                                )}
                                
                                {/* Task expired */}
                                {launch.task_expired && !hasSubmitted && (
                                  <span className="text-xs text-red-400 font-bold">
                                    ⏰ Tiempo expirado
                                  </span>
                                )}
                              </div>
                              
                              {/* Upload Screenshot Section - After joining (only within upload window: 2-12 min) */}
                              {hasJoined && !hasSubmitted && launch.upload_window_started && !launch.upload_expired && (
                                <div className="mt-2 p-3 bg-[#FF6B6B]/10 rounded-lg border border-[#FF6B6B]/30">
                                  <p className="text-sm text-gray-300 mb-2">
                                    📸 Sube una captura del video para recibir tu recompensa
                                  </p>
                                  <input
                                    type="file"
                                    ref={el => launchScreenshotRefs.current[launch.id] = el}
                                    className="hidden"
                                    accept="image/*"
                                    onChange={(e) => {
                                      if (e.target.files[0]) {
                                        handleUploadLaunchScreenshot(launch.id, e.target.files[0]);
                                      }
                                    }}
                                  />
                                  <Button
                                    onClick={() => launchScreenshotRefs.current[launch.id]?.click()}
                                    disabled={uploadingLaunchScreenshot[launch.id]}
                                    className="w-full bg-[#FF6B6B] hover:bg-[#FF6B6B]/80 text-white font-bold"
                                  >
                                    {uploadingLaunchScreenshot[launch.id] ? (
                                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Subiendo...</>
                                    ) : (
                                      <><Camera className="w-4 h-4 mr-2" /> Subir Captura de Pantalla</>
                                    )}
                                  </Button>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Assigned Tasks for Pro Users */}
              {assignedTasks.length > 0 && (
                <Card className={`border-2 ${assignedTasks.some(t => t.status === 'pending') 
                  ? 'bg-gradient-to-br from-[#22C55E]/20 to-[#22C55E]/5 border-[#22C55E]' 
                  : 'bg-[#121212] border-white/10'}`}>
                  <CardHeader>
                    <CardTitle className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-lg">
                        <div className={`p-2 rounded-lg ${assignedTasks.some(t => t.status === 'pending') 
                          ? 'bg-[#22C55E]' 
                          : 'bg-[#22C55E]/20'}`}>
                          <ClipboardList className={`w-5 h-5 ${assignedTasks.some(t => t.status === 'pending') 
                            ? 'text-black' 
                            : 'text-[#22C55E]'}`} />
                        </div>
                        <span>Mis Tareas</span>
                      </div>
                      {assignedTasks.filter(t => t.status === 'pending').length > 0 && (
                        <span className="px-3 py-1 bg-[#22C55E] text-black text-sm font-bold rounded-full animate-pulse">
                          {assignedTasks.filter(t => t.status === 'pending').length} PENDIENTE{assignedTasks.filter(t => t.status === 'pending').length > 1 ? 'S' : ''}
                        </span>
                      )}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {assignedTasks.map((task) => (
                      <div key={task.id} className={`rounded-lg p-4 space-y-3 ${
                        task.status === 'pending' 
                          ? 'bg-[#22C55E]/10 border-2 border-[#22C55E]/50' 
                          : 'bg-[#1E1E1E]'
                      }`}>
                        <div className="flex items-start justify-between">
                          <div>
                            <h4 className="font-bold text-white">{task.title}</h4>
                            <p className="text-sm text-gray-400">{task.description}</p>
                          </div>
                          <span className={`px-2 py-1 rounded text-xs font-bold ${
                            task.status === 'pending' ? 'bg-yellow-500 text-black' :
                            task.status === 'completed' ? 'bg-blue-500/20 text-blue-500' :
                            task.status === 'approved' ? 'bg-green-500/20 text-green-500' :
                            'bg-red-500/20 text-red-500'
                          }`}>
                            {task.status === 'pending' ? '⚡ PENDIENTE' :
                             task.status === 'completed' ? 'En revisión' :
                             task.status === 'approved' ? '✓ Aprobada' : 'Rechazada'}
                          </span>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-[#22C55E] font-bold text-lg">💰 RD${task.reward}</span>
                          {task.task_url && (
                            <a href={task.task_url} target="_blank" rel="noopener noreferrer" 
                               className="px-3 py-1 bg-[#D946EF] text-white rounded-full flex items-center gap-1 text-xs font-bold hover:bg-[#D946EF]/80">
                              <ExternalLink className="w-3 h-3" /> IR A LA TAREA
                            </a>
                          )}
                        </div>
                        {task.status === 'pending' && (
                          <div className="pt-2">
                            <input
                              type="file"
                              id={`pro-task-file-${task.id}`}
                              className="hidden"
                              accept="image/*"
                              onChange={(e) => {
                                if (e.target.files[0]) {
                                  handleCompleteAssignedTask(task.id, e.target.files[0]);
                                }
                              }}
                            />
                            <Button
                              onClick={() => document.getElementById(`pro-task-file-${task.id}`).click()}
                              disabled={uploadingTaskScreenshot[task.id]}
                              size="lg"
                              className="w-full bg-[#22C55E] hover:bg-[#22C55E]/80 text-black font-bold text-base py-6"
                            >
                              {uploadingTaskScreenshot[task.id] ? (
                                <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Subiendo...</>
                              ) : (
                                <><Camera className="w-5 h-5 mr-2" /> 📸 SUBIR CAPTURA Y GANAR</>
                              )}
                            </Button>
                          </div>
                        )}
                        {task.screenshot_url && (
                          <div className="pt-2">
                            <img 
                              src={`${BACKEND_URL}${task.screenshot_url}`} 
                              alt="Captura" 
                              className="w-full h-32 object-cover rounded-lg border border-white/10"
                            />
                          </div>
                        )}
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* Daily Visits Section - Available for ALL users (Recluta and Pro) */}
              {dailyVisits.length > 0 && (
                <Card className={`border-2 ${dailyVisits.some(v => v.can_submit && v.spots_remaining > 0) 
                  ? 'bg-gradient-to-br from-[#FF6B6B]/20 to-[#FF8E53]/5 border-[#FF6B6B]' 
                  : 'bg-[#121212] border-white/10'}`}>
                  <CardHeader>
                    <CardTitle className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-lg">
                        <div className={`p-2 rounded-lg ${dailyVisits.some(v => v.can_submit) 
                          ? 'bg-[#FF6B6B]' 
                          : 'bg-[#FF6B6B]/20'}`}>
                          <Eye className={`w-5 h-5 ${dailyVisits.some(v => v.can_submit) 
                            ? 'text-white' 
                            : 'text-[#FF6B6B]'}`} />
                        </div>
                        <span>Visitas Diarias</span>
                      </div>
                      <div className="flex gap-2">
                        {dailyVisitsStats.total_completed_today > 0 && (
                          <span className="px-2 py-1 bg-green-500/20 text-green-500 text-xs rounded">
                            ✓ {dailyVisitsStats.total_completed_today} aprobadas
                          </span>
                        )}
                        {dailyVisitsStats.total_pending > 0 && (
                          <span className="px-2 py-1 bg-yellow-500/20 text-yellow-500 text-xs rounded">
                            ⏳ {dailyVisitsStats.total_pending} pendientes
                          </span>
                        )}
                      </div>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {dailyVisits.map((visit) => (
                      <div key={visit.id} className={`rounded-lg p-4 space-y-3 ${
                        visit.can_submit && visit.spots_remaining > 0
                          ? 'bg-[#FF6B6B]/10 border-2 border-[#FF6B6B]/50' 
                          : 'bg-[#1E1E1E]'
                      }`}>
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-3">
                            {getPlatformIcon(visit.platform)}
                            <div>
                              <h4 className="font-bold text-white">{visit.title}</h4>
                              <p className="text-sm text-gray-400 capitalize">{visit.platform}</p>
                              {/* Fecha y hora de creación */}
                              {visit.created_at && (
                                <p className="text-xs text-[#FFD700] flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  {new Date(visit.created_at).toLocaleString('es-DO', { 
                                    dateStyle: 'short', 
                                    timeStyle: 'short' 
                                  })}
                                </p>
                              )}
                              {visit.user_completed_count > 0 && (
                                <p className="text-xs text-green-500">✓ {visit.user_completed_count} completada(s) hoy</p>
                              )}
                            </div>
                          </div>
                          {visit.user_pending_count > 0 && (
                            <span className="px-2 py-1 bg-yellow-500 text-black text-xs font-bold rounded">
                              ⏳ {visit.user_pending_count} en revisión
                            </span>
                          )}
                        </div>
                        
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-[#FF6B6B] font-bold text-lg">💰 RD${visit.reward_amount}</span>
                          
                          {/* Timer Display */}
                          {visitTimers[visit.id]?.started && (
                            <div className={`px-3 py-1 rounded-full text-sm font-bold ${
                              visitTimers[visit.id]?.canSubmit 
                                ? 'bg-green-500/20 text-green-500' 
                                : 'bg-yellow-500/20 text-yellow-500 animate-pulse'
                            }`}>
                              ⏱️ {visitTimers[visit.id]?.elapsedFormatted || '0:00'}
                              {!visitTimers[visit.id]?.canSubmit && ' (mín 1:20)'}
                            </div>
                          )}
                          
                          {/* Start Visit Button - Always available */}
                          {!visitTimers[visit.id]?.started && (
                            <button 
                              onClick={() => handleStartVisit(visit.id, visit.target_url)}
                              className="px-3 py-1 bg-gradient-to-r from-[#FF6B6B] to-[#FF8E53] text-white rounded-full flex items-center gap-1 text-xs font-bold hover:opacity-80"
                            >
                              <ExternalLink className="w-3 h-3" /> IR A VISITAR
                            </button>
                          )}
                        </div>
                        
                        {/* Timer Message */}
                        {visitTimers[visit.id]?.started && !visitTimers[visit.id]?.canSubmit && (
                          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-2 text-center mt-2">
                            <p className="text-yellow-500 text-xs">⏳ Espera al menos 1:20 en la visita para poder subir la captura</p>
                          </div>
                        )}
                        
                        {/* Upload Button - Show when timer allows */}
                        {visitTimers[visit.id]?.canSubmit && (
                          <div className="pt-2">
                            <input
                              type="file"
                              id={`visit-file-pro-${visit.id}`}
                              className="hidden"
                              accept="image/*"
                              onChange={(e) => {
                                if (e.target.files[0]) {
                                  handleCompleteDailyVisit(visit.id, e.target.files[0]);
                                }
                              }}
                            />
                            <Button
                              onClick={() => document.getElementById(`visit-file-pro-${visit.id}`).click()}
                              disabled={uploadingVisitScreenshot[visit.id]}
                              size="lg"
                              className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:opacity-80 text-white font-bold text-base py-6"
                            >
                              {uploadingVisitScreenshot[visit.id] ? (
                                <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Subiendo...</>
                              ) : (
                                <><Camera className="w-5 h-5 mr-2" /> ✅ SUBIR CAPTURA ({visitTimers[visit.id]?.elapsedFormatted})</>
                              )}
                            </Button>
                          </div>
                        )}
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* User Tier Badge & Pro Upgrade */}
              <Card className={`border-white/10 ${
                proStatus?.user_tier === 'pro' && proStatus?.pro_status === 'approved'
                  ? 'bg-gradient-to-r from-[#FFD700]/20 to-[#FFA500]/20 border-yellow-500/40'
                  : 'bg-[#121212]'
              }`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      {proStatus?.user_tier === 'pro' && proStatus?.pro_status === 'approved' ? (
                        <>
                          <Crown className="w-6 h-6 text-yellow-500" />
                          <span className="font-bold text-yellow-500 text-lg">Usuario PRO</span>
                        </>
                      ) : (
                        <>
                          <User className="w-6 h-6 text-gray-400" />
                          <span className="font-bold text-gray-400 text-lg">Recluta</span>
                        </>
                      )}
                    </div>
                    {proStatus?.pro_status === 'pending' && (
                      <span className="px-2 py-1 text-xs bg-yellow-500/20 text-yellow-400 rounded-full">
                        ⏳ Pendiente
                      </span>
                    )}
                    {proStatus?.pro_status === 'awaiting_approval' && (
                      <span className="px-2 py-1 text-xs bg-blue-500/20 text-blue-400 rounded-full">
                        📋 En revisión
                      </span>
                    )}
                  </div>

                  {proStatus?.user_tier !== 'pro' || proStatus?.pro_status !== 'approved' ? (
                    <div className="space-y-3">
                      <div className="p-3 bg-gradient-to-r from-[#FFD700]/10 to-[#FFA500]/10 rounded-lg border border-yellow-500/30">
                        <p className="font-bold text-white mb-1">🌟 Hazte Usuario PRO</p>
                        <p className="text-sm text-gray-400 mb-2">Accede a todas las tareas y gana más dinero</p>
                        <p className="text-2xl font-bold text-[#22C55E]">RD$1,100</p>
                        <p className="text-xs text-gray-500">Pago único</p>
                      </div>

                      {proStatus?.pro_status === 'pending' && proStatus?.upgrade_request && !proStatus?.upgrade_request?.receipt_url ? (
                        <div className="space-y-2">
                          <p className="text-sm text-yellow-400">Sube tu comprobante de pago:</p>
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            ref={proReceiptRef}
                            onChange={(e) => e.target.files[0] && handleUploadProReceipt(e.target.files[0])}
                          />
                          <Button
                            className="w-full bg-gradient-to-r from-[#FFD700] to-[#FFA500] text-black font-bold"
                            onClick={() => proReceiptRef.current?.click()}
                            disabled={uploadingProReceipt}
                          >
                            {uploadingProReceipt ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Camera className="w-4 h-4 mr-2" />}
                            Subir Comprobante
                          </Button>
                        </div>
                      ) : proStatus?.pro_status === 'awaiting_approval' ? (
                        <div className="p-3 bg-blue-500/10 rounded-lg border border-blue-500/30 text-center">
                          <Clock className="w-8 h-8 text-blue-400 mx-auto mb-2" />
                          <p className="text-sm text-blue-400">Tu solicitud está siendo revisada</p>
                        </div>
                      ) : proStatus?.pro_status !== 'approved' ? (
                        <Button
                          className="w-full bg-gradient-to-r from-[#FFD700] to-[#FFA500] text-black font-bold h-12"
                          onClick={handleRequestProUpgrade}
                        >
                          <Crown className="w-5 h-5 mr-2" />
                          Solicitar Upgrade a PRO
                        </Button>
                      ) : null}
                    </div>
                  ) : (
                    <div className="text-center p-3 bg-yellow-500/10 rounded-lg border border-yellow-500/30">
                      <Star className="w-8 h-8 text-yellow-500 mx-auto mb-2" />
                      <p className="text-sm text-yellow-400">¡Tienes acceso completo a todas las tareas!</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Assigned Tasks for Reclutas */}
              {assignedTasks.length > 0 && (
                <Card className="bg-[#121212] border-white/10">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <ClipboardList className="w-5 h-5 text-purple-500" />
                      Mis Tareas Asignadas ({assignedTasks.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {assignedTasks.map((task) => (
                      <div
                        key={task.id}
                        className={`p-3 rounded-lg border ${
                          task.status === 'approved' 
                            ? 'bg-green-500/10 border-green-500/30'
                            : task.status === 'completed'
                            ? 'bg-blue-500/10 border-blue-500/30'
                            : 'bg-[#1E1E1E] border-white/10'
                        }`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <p className="font-bold">{task.title}</p>
                            <p className="text-sm text-gray-400">{task.description}</p>
                            {task.task_url && (
                              <a
                                href={task.task_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-blue-400 hover:underline flex items-center gap-1 mt-1"
                              >
                                <ExternalLink className="w-3 h-3" /> Ver enlace
                              </a>
                            )}
                          </div>
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            task.status === 'approved' 
                              ? 'bg-green-500/20 text-green-400'
                              : task.status === 'completed'
                              ? 'bg-blue-500/20 text-blue-400'
                              : 'bg-yellow-500/20 text-yellow-400'
                          }`}>
                            {task.status === 'approved' ? '✓ Aprobada' : task.status === 'completed' ? '⏳ En revisión' : '📋 Pendiente'}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <p className="text-lg font-bold text-[#22C55E]">+RD${task.reward}</p>
                          {task.status === 'pending' && (
                            <>
                              <input
                                type="file"
                                accept="image/*"
                                className="hidden"
                                ref={el => assignedTaskRef.current = el}
                                onChange={(e) => e.target.files[0] && handleCompleteAssignedTask(task.id, e.target.files[0])}
                              />
                              <Button
                                size="sm"
                                className="bg-purple-500 hover:bg-purple-600"
                                onClick={() => assignedTaskRef.current?.click()}
                                disabled={uploadingTaskScreenshot[task.id]}
                              >
                                {uploadingTaskScreenshot[task.id] ? (
                                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                                ) : (
                                  <Camera className="w-4 h-4 mr-1" />
                                )}
                                Subir Prueba
                              </Button>
                            </>
                          )}
                        </div>
                        {task.rejection_reason && (
                          <p className="text-xs text-red-400 mt-2">
                            ❌ Rechazada: {task.rejection_reason}
                          </p>
                        )}
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

            </>
          )}

          {/* Logout Button */}
          <Button
            onClick={handleLogout}
            data-testid="logout-btn"
            variant="outline"
            className="w-full border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Cerrar Sesión
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-20 p-4 animate-fadeInUp" data-testid="user-auth">
      <div className="max-w-md mx-auto">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2">
            <h1 className="text-4xl font-bold font-serif tracking-wider"
                style={{
                  background: 'linear-gradient(180deg, #FFF8DC 0%, #FFD700 30%, #B8860B 70%, #8B7500 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}>
              LEYBA
            </h1>
            <span className="text-xs font-bold px-2 py-0.5 rounded text-black"
                  style={{
                    background: 'linear-gradient(135deg, #FFD700 0%, #F4C430 50%, #DAA520 100%)',
                  }}>
              PRO
            </span>
          </div>
          <p className="text-[#B8860B] mt-2 text-sm tracking-widest">ENTERTAINMENT</p>
        </div>

        <Card className="bg-[#121212] border-white/10">
          <CardContent className="p-6">
            <Tabs value={authTab} onValueChange={setAuthTab}>
              <TabsList className="grid w-full grid-cols-2 bg-[#1E1E1E]">
                <TabsTrigger value="login" data-testid="login-tab">Iniciar Sesión</TabsTrigger>
                <TabsTrigger value="register" data-testid="register-tab">Registrarse</TabsTrigger>
              </TabsList>

              <TabsContent value="login" className="mt-6">
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="login-phone">Teléfono</Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        id="login-phone"
                        data-testid="login-phone"
                        type="tel"
                        placeholder="809-123-4567"
                        className="pl-10 bg-[#1E1E1E] border-white/10"
                        value={loginForm.phone}
                        onChange={(e) => setLoginForm({ ...loginForm, phone: e.target.value })}
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="login-password">Contraseña</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        id="login-password"
                        data-testid="login-password"
                        type={showPassword ? "text" : "password"}
                        placeholder="••••••••"
                        className="pl-10 pr-10 bg-[#1E1E1E] border-white/10"
                        value={loginForm.password}
                        onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="admin-login"
                      data-testid="admin-login-checkbox"
                      checked={isAdminLogin}
                      onChange={(e) => setIsAdminLogin(e.target.checked)}
                      className="rounded border-white/20"
                    />
                    <Label htmlFor="admin-login" className="text-sm text-gray-400 cursor-pointer">
                      Iniciar como Administrador
                    </Label>
                  </div>

                  <Button
                    type="submit"
                    data-testid="login-submit"
                    disabled={loading}
                    className="w-full bg-[#D946EF] hover:bg-[#D946EF]/80 rounded-xl h-12 text-lg font-bold"
                  >
                    {loading ? "Cargando..." : "Entrar"}
                  </Button>

                  <button
                    type="button"
                    onClick={() => setShowForgotPassword(true)}
                    className="w-full text-sm text-[#D946EF] hover:underline"
                  >
                    ¿Olvidaste tu contraseña?
                  </button>
                </form>

                {/* Forgot Password Modal */}
                {showForgotPassword && (
                  <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
                    <Card className="bg-[#121212] border-white/10 w-full max-w-md">
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <Lock className="w-5 h-5 text-[#D946EF]" />
                          Recuperar Contraseña
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <p className="text-sm text-gray-400">
                          Ingresa tu número de teléfono y te conectaremos con el administrador por WhatsApp para recuperar tu contraseña.
                        </p>
                        <div className="space-y-2">
                          <Label>Número de Teléfono</Label>
                          <Input
                            type="tel"
                            placeholder="809-123-4567"
                            className="bg-[#1E1E1E] border-white/10"
                            value={forgotPhone}
                            onChange={(e) => setForgotPhone(e.target.value)}
                          />
                        </div>
                        <div className="flex gap-2">
                          <Button
                            onClick={() => setShowForgotPassword(false)}
                            variant="outline"
                            className="flex-1 border-white/20"
                          >
                            Cancelar
                          </Button>
                          <Button
                            onClick={handleForgotPassword}
                            disabled={loading}
                            className="flex-1 bg-[#25D366] hover:bg-[#25D366]/80"
                          >
                            {loading ? "..." : "Contactar por WhatsApp"}
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="register" className="mt-6">
                <form onSubmit={handleRegister} className="space-y-4">
                  {/* User Type Selection */}
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <button
                      type="button"
                      onClick={() => setRegisterForm({ ...registerForm, user_tier: 'recluta' })}
                      className={`p-4 rounded-xl border-2 transition-all ${
                        registerForm.user_tier === 'recluta'
                          ? 'border-[#22C55E] bg-[#22C55E]/10'
                          : 'border-white/10 bg-[#1E1E1E]'
                      }`}
                    >
                      <User className={`w-8 h-8 mx-auto mb-2 ${registerForm.user_tier === 'recluta' ? 'text-[#22C55E]' : 'text-gray-400'}`} />
                      <p className="font-bold text-sm">Recluta</p>
                      <p className="text-xs text-gray-400">Gratis</p>
                      <p className="text-[10px] text-gray-500 mt-1">Solo tareas asignadas</p>
                    </button>
                    <button
                      type="button"
                      onClick={() => setRegisterForm({ ...registerForm, user_tier: 'pro' })}
                      className={`p-4 rounded-xl border-2 transition-all ${
                        registerForm.user_tier === 'pro'
                          ? 'border-yellow-500 bg-yellow-500/10'
                          : 'border-white/10 bg-[#1E1E1E]'
                      }`}
                    >
                      <Crown className={`w-8 h-8 mx-auto mb-2 ${registerForm.user_tier === 'pro' ? 'text-yellow-500' : 'text-gray-400'}`} />
                      <p className="font-bold text-sm">PRO</p>
                      <p className="text-xs text-yellow-500">RD$1,100</p>
                      <p className="text-[10px] text-gray-500 mt-1">Acceso completo</p>
                    </button>
                  </div>

                  {registerForm.user_tier === 'pro' && (
                    <div className="p-3 bg-yellow-500/10 rounded-lg border border-yellow-500/30 mb-4">
                      <p className="text-xs text-yellow-400">
                        💡 Después de registrarte, deberás pagar <strong>RD$1,100</strong> y subir el comprobante para activar tu cuenta Pro.
                      </p>
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="reg-nickname">Apodo</Label>
                    <Input
                      id="reg-nickname"
                      data-testid="register-nickname"
                      placeholder="Tu apodo"
                      className="bg-[#1E1E1E] border-white/10"
                      value={registerForm.nickname}
                      onChange={(e) => setRegisterForm({ ...registerForm, nickname: e.target.value })}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="reg-phone">Teléfono</Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        id="reg-phone"
                        data-testid="register-phone"
                        type="tel"
                        placeholder="809-123-4567"
                        className="pl-10 bg-[#1E1E1E] border-white/10"
                        value={registerForm.phone}
                        onChange={(e) => setRegisterForm({ ...registerForm, phone: e.target.value })}
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="reg-country">País</Label>
                      <select
                        id="reg-country"
                        className="w-full h-10 bg-[#1E1E1E] border border-white/10 rounded-md px-3 text-white"
                        value={registerForm.country}
                        onChange={(e) => setRegisterForm({ ...registerForm, country: e.target.value })}
                        required
                      >
                        <option value="">Seleccionar país</option>
                        <option value="República Dominicana">🇩🇴 República Dominicana</option>
                        <option value="Estados Unidos">🇺🇸 Estados Unidos</option>
                        <option value="Puerto Rico">🇵🇷 Puerto Rico</option>
                        <option value="España">🇪🇸 España</option>
                        <option value="México">🇲🇽 México</option>
                        <option value="Colombia">🇨🇴 Colombia</option>
                        <option value="Venezuela">🇻🇪 Venezuela</option>
                        <option value="Argentina">🇦🇷 Argentina</option>
                        <option value="Chile">🇨🇱 Chile</option>
                        <option value="Perú">🇵🇪 Perú</option>
                        <option value="Otro">Otro</option>
                      </select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="reg-city">Ciudad/Lugar</Label>
                      <Input
                        id="reg-city"
                        placeholder="Santo Domingo"
                        className="bg-[#1E1E1E] border-white/10"
                        value={registerForm.city}
                        onChange={(e) => setRegisterForm({ ...registerForm, city: e.target.value })}
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="reg-password">Contraseña</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        id="reg-password"
                        data-testid="register-password"
                        type={showPassword ? "text" : "password"}
                        placeholder="••••••••"
                        className="pl-10 pr-10 bg-[#1E1E1E] border-white/10"
                        value={registerForm.password}
                        onChange={(e) => setRegisterForm({ ...registerForm, password: e.target.value })}
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="reg-confirm-password">Confirmar Contraseña</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        id="reg-confirm-password"
                        data-testid="register-confirm-password"
                        type={showPassword ? "text" : "password"}
                        placeholder="••••••••"
                        className="pl-10 bg-[#1E1E1E] border-white/10"
                        value={registerForm.confirm_password}
                        onChange={(e) => setRegisterForm({ ...registerForm, confirm_password: e.target.value })}
                        required
                      />
                    </div>
                    {registerForm.password && registerForm.confirm_password && registerForm.password !== registerForm.confirm_password && (
                      <p className="text-xs text-red-500">Las contraseñas no coinciden</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="reg-referral">Código de Referido (Opcional)</Label>
                    <div className="relative">
                      <Users className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        id="reg-referral"
                        data-testid="register-referral"
                        placeholder="ABC12345"
                        className="pl-10 bg-[#1E1E1E] border-white/10"
                        value={registerForm.referral_code}
                        onChange={(e) => setRegisterForm({ ...registerForm, referral_code: e.target.value })}
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    data-testid="register-submit"
                    disabled={loading}
                    className="w-full bg-[#22C55E] hover:bg-[#22C55E]/80 rounded-xl h-12 text-lg font-bold text-black"
                  >
                    {loading ? "Registrando..." : "Crear Cuenta"}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default UserPage;
